const DEBUG_HELP = true; // flip to true to log hint-lookup diagnostics

// ========== STATE ==========
let canvas;
let tutorialManager;
let presetManager;
let startChooser;
let hintLookup;
let jsxManagerParent = null;
let jsxManagerResult = null;
let graphingManagerParent = null;
let expressions = [];
let colorIndex = 0;
let observationStore, observationMatcher, thinkingPanel;

// Parent function state
let parentFunction = null;
let parentFunctionType = null;
let parentExprData = null;
let parentSelectMode = 'simple'; // 'simple' | 'complex'

// Active modal canvas (for walkthrough capture)
let activeModalCanvas = null;
let activeModalAffordance = null;

// Form manager for alternate surface forms (rewrites)
let formManager = null;
let exprSystem = null;
let transformFunctionFlow = null;

// Point rule state (created via PointRuleFlow)
let pointRuleExpression = null; // e.g., "(x,y)\\to (x+3,y-2)"
let pointRuleEvaluators = null; // { evaluateXRule, evaluateYRule }
let pointRuleFlow = null; // PointRuleFlow instance

// ========== CHAIN STATE ==========
const chainState = {
  params: { a: 1, b: 1, h: 0, k: 0 },
  history: [], // [{ type, value, prevParams }]
  stepIndex: 0,
};

let chainDLs = []; // [{ label, derivation, formula, graphFormula, color, params, labelButton, type }]
const CHAIN_START_Y = 150;
const CHAIN_STEP_SPACING = 76; // Proportional to font size 40 (was 95 for font size 50)

const SUBSCRIPT_DIGITS = [
  '\u2080',
  '\u2081',
  '\u2082',
  '\u2083',
  '\u2084',
  '\u2085',
  '\u2086',
  '\u2087',
  '\u2088',
  '\u2089',
];
function subscriptNum(n) {
  return String(n)
    .split('')
    .map((d) => SUBSCRIPT_DIGITS[+d])
    .join('');
}

// ========== DYNAMIC EXPRESSION SIZING ==========
// Scale expression font size to fit available width before reason overlay
const REASON_OVERLAY_X = 580; // .step-reason { left: 580px }
const MAX_FONT_SIZE = 40;
const MIN_FONT_SIZE = 20;
const FONT_SIZE_PADDING = 30; // Extra padding before reason text

/**
 * Adjust a derivation's font size so it doesn't overlap the reason text.
 * @param {Object} derivation - The gmath derivation object (from createDL callback)
 * @param {number} startX - The x position where the expression starts
 * @param {number} startY - The y position where the expression starts
 */
function fitExpressionToWidth(derivation, startX, startY) {
  if (!derivation || !derivation.size) return;

  const availableWidth = REASON_OVERLAY_X - startX - FONT_SIZE_PADDING;
  const currentWidth = derivation.size.width;
  const currentFontSize = derivation.settings?.get('font_size') || MAX_FONT_SIZE;

  if (currentWidth > availableWidth && availableWidth > 0) {
    // Scale down proportionally
    const scale = availableWidth / currentWidth;
    const newFontSize = Math.max(MIN_FONT_SIZE, Math.floor(currentFontSize * scale));
    if (newFontSize < currentFontSize) {
      derivation.updateFontSize(newFontSize);
      // Reset position to keep left-aligned after font size change
      derivation.translateElement({ x: startX, y: startY });
    }
  }
}
// ========== FUNCTION NAMING MODES ==========
// Letter sequence for distinct-letter naming: g, h, j, k, m, n, p, q, r, s, t, u, v, w
// Skips: e (Euler), f (parent), i (imaginary), l (looks like 1), o (looks like 0), x (variable)
const LETTER_SEQUENCE = 'ghjkmnpqrstuvw'.split('');

function isLetterNamingMode() {
  const el = document.getElementById('naming-letter');
  return el?.checked === true;
}

// GM ASCII label for chain step DLs: f_1 (subscript mode) or g (letter mode)
function getStepGMLabel(index) {
  if (isLetterNamingMode()) {
    return LETTER_SEQUENCE[index % LETTER_SEQUENCE.length];
  }
  return 'f_' + (index + 1);
}
// Display label for buttons/observations: f₁ (subscript mode) or g (letter mode)
function getStepDisplayLabel(index) {
  if (isLetterNamingMode()) {
    return LETTER_SEQUENCE[index % LETTER_SEQUENCE.length];
  }
  return 'f' + subscriptNum(index + 1);
}
// The final accumulated result is always called g (subscript) or the last letter used
function currentResultLabel() {
  return 'g';
}

// ========== TERMINOLOGY HELPERS ==========
// Returns 'shift' or 'translation' based on settings
function getShiftTerm() {
  const el = document.getElementById('term-shift-translation');
  return el && el.checked ? 'translation' : 'shift';
}

// Returns 'stretch' or 'dilation' based on settings
function getScaleTerm() {
  const el = document.getElementById('term-scale-dilation');
  return el && el.checked ? 'dilation' : 'stretch';
}

// Terminology lookup tables
const TERMINOLOGY = {
  shift: {
    'h-shift': 'Horizontal Shift',
    'v-shift': 'Vertical Shift',
    'h-shift-right': 'Shift right',
    'h-shift-left': 'Shift left',
    'v-shift-up': 'Shift up',
    'v-shift-down': 'Shift down',
  },
  translation: {
    'h-shift': 'Horizontal Translation',
    'v-shift': 'Vertical Translation',
    'h-shift-right': 'Translate right',
    'h-shift-left': 'Translate left',
    'v-shift-up': 'Translate up',
    'v-shift-down': 'Translate down',
  },
  stretch: {
    'v-stretch': 'Vertical Stretch',
    'h-stretch': 'Horizontal Stretch',
    'v-stretch-expand': 'Stretch vertically',
    'v-stretch-compress': 'Compress vertically',
    'h-stretch-expand': 'Stretch horizontally',
    'h-stretch-compress': 'Compress horizontally',
  },
  dilation: {
    'v-stretch': 'Vertical Dilation',
    'h-stretch': 'Horizontal Dilation',
    'v-stretch-expand': 'Dilate vertically (expand)',
    'v-stretch-compress': 'Dilate vertically (compress)',
    'h-stretch-expand': 'Dilate horizontally (expand)',
    'h-stretch-compress': 'Dilate horizontally (compress)',
  },
};

// Get localized label for a transform type
function getTransformLabel(type) {
  if (type === 'h-shift' || type === 'v-shift') {
    return TERMINOLOGY[getShiftTerm()][type];
  }
  if (type === 'h-stretch' || type === 'v-stretch') {
    return TERMINOLOGY[getScaleTerm()][type];
  }
  // Reflections don't vary
  if (type === 'reflect-x') return 'Reflect across x-axis';
  if (type === 'reflect-y') return 'Reflect across y-axis';
  return type;
}

// ========== PARAMETER UPDATE MATH (ORDER-AWARE) ==========
function applyTransformation(type, value) {
  const prev = { ...chainState.params };
  const p = chainState.params;

  switch (type) {
    case 'h-shift':
      p.h += value;
      break;
    case 'v-shift':
      p.k += value;
      break;
    case 'h-stretch':
      p.h /= value;
      p.b *= value;
      break;
    case 'v-stretch':
      p.a *= value;
      p.k *= value;
      break;
    case 'reflect-x':
      p.a = -p.a;
      p.k = -p.k;
      break;
    case 'reflect-y':
      p.b = -p.b;
      p.h = -p.h;
      break;
  }

  chainState.history.push({ type, value, prevParams: prev });
  chainState.stepIndex++;
}

// Format a numeric value for display, converting decimals to fractions when appropriate
function formatValueForDisplay(val) {
  const absVal = Math.abs(val);
  const sign = val < 0 ? '-' : '';

  // If it's already an integer, just show it
  if (Number.isInteger(absVal)) {
    return sign + String(absVal);
  }

  // Check for simple fractions (up to denominator 10)
  for (let denom = 2; denom <= 10; denom++) {
    const numerator = absVal * denom;
    if (Math.abs(numerator - Math.round(numerator)) < 0.0001) {
      const num = Math.round(numerator);
      if (num !== denom) {
        // Don't show 2/2, just show 1
        return sign + num + '/' + denom;
      }
    }
  }

  // Fall back to the numeric value
  return String(val);
}

function getStepDescription(type, value) {
  const shiftTerms = TERMINOLOGY[getShiftTerm()];
  const scaleTerms = TERMINOLOGY[getScaleTerm()];
  const displayVal = formatValueForDisplay(Math.abs(value));

  switch (type) {
    case 'h-shift':
      return value > 0
        ? shiftTerms['h-shift-right'] + ' by ' + displayVal
        : shiftTerms['h-shift-left'] + ' by ' + displayVal;
    case 'v-shift':
      return value > 0
        ? shiftTerms['v-shift-up'] + ' by ' + displayVal
        : shiftTerms['v-shift-down'] + ' by ' + displayVal;
    case 'h-stretch':
      if (Math.abs(value) > 1)
        return scaleTerms['h-stretch-expand'] + ' by ' + displayVal;
      return scaleTerms['h-stretch-compress'] + ' by ' + displayVal;
    case 'v-stretch':
      if (Math.abs(value) > 1)
        return scaleTerms['v-stretch-expand'] + ' by ' + displayVal;
      return scaleTerms['v-stretch-compress'] + ' by ' + displayVal;
    case 'reflect-x':
      return 'Reflect across x-axis';
    case 'reflect-y':
      return 'Reflect across y-axis';
    default:
      return type;
  }
}

function createReasonOverlay(text, yPos) {
  const el = document.createElement('div');
  el.className = 'step-reason';
  el.textContent = text;
  el.style.top = yPos + 'px';
  document.getElementById('gm-page-container').appendChild(el);
  return el;
}

function undoLastTransformation() {
  if (chainState.history.length === 0) return;
  const last = chainState.history.pop();
  chainState.params = { ...last.prevParams };
  chainState.stepIndex--;
  removeLastChainDL();
  onChainChanged();
}

function resetAllTransformations() {
  if (chainState.history.length === 0) return;
  chainState.params = { a: 1, b: 1, h: 0, k: 0 };
  chainState.history = [];
  chainState.stepIndex = 0;
  removeAllChainDLs();

  // Clear point rule state
  pointRuleExpression = null;
  pointRuleEvaluators = null;
  clearGptArrows();

  onChainChanged();
}

function onChainChanged() {
  // Recompute and update result graph
  if (parentFunctionType) {
    const parentFormula = PARENT_FUNCTIONS[parentFunctionType].formula;
    if (chainState.history.length > 0) {
      const resultFormula = computeTransformedFormula(parentFormula, chainState.params);
      updateResultGraph('y = ' + resultFormula);
    } else {
      updateResultGraph(null);
    }
  }

  updateUndoResetButtons();
  updateParamDisplay();
  updateResultFunctionBox();
  updateSidebarCardStates();
  if (typeof updateMorphPlayButtons === 'function') updateMorphPlayButtons();

  // Update result graph header text (preserve child elements like Clear Arrows btn)
  const header = document.getElementById('result-graph-header');
  if (header) {
    const text = chainState.history.length === 0 ? 'Result' : 'g(x) \u2014 Result';
    const textNode = header.firstChild;
    if (textNode && textNode.nodeType === Node.TEXT_NODE) {
      textNode.textContent = text;
    } else {
      header.insertBefore(document.createTextNode(text), header.firstChild);
    }
  }
}

function updateParamDisplay() {
  const el = document.getElementById('param-display');
  if (!el) return;

  // Keep hidden - the a,b,h,k panel adds clutter without much benefit
  el.style.display = 'none';

  const defaults = { a: 1, b: 1, h: 0, k: 0 };
  const p = chainState.params;

  ['a', 'b', 'h', 'k'].forEach((key) => {
    const valEl = document.getElementById('param-val-' + key);
    const rowEl = valEl?.closest('.param-row');
    if (!valEl) return;

    const val = p[key];
    const isDefault = val === defaults[key];
    valEl.textContent = fmtNum(val);
    valEl.classList.toggle('default-val', isDefault);
    if (rowEl) rowEl.classList.toggle('changed', !isDefault);
  });
}

// ========== RESULT FUNCTION BOX ==========
let _resultBoxCanvas = null;
let _resultBoxDL = null;
let _resultBoxPointRuleDL = null;

function updateResultFunctionBox() {
  const box = document.getElementById('result-function-box');
  if (!box) return;

  const hasTransformations = chainState.history.length > 0;
  const hasPointRule = !!pointRuleExpression;

  // Hide if no parent, OR if no transformations AND no point rule
  if (!parentFunctionType || (!hasTransformations && !hasPointRule)) {
    box.style.display = 'none';
    box.classList.remove('has-point-rule');
    if (_resultBoxCanvas) {
      try {
        _resultBoxCanvas.destroy();
      } catch (e) {}
      _resultBoxCanvas = null;
      _resultBoxDL = null;
      _resultBoxPointRuleDL = null;
    }
    return;
  }

  box.style.display = '';

  // Compute result formula only if there are transformations
  let formula = null;
  if (hasTransformations) {
    const parentFormula = PARENT_FUNCTIONS[parentFunctionType].formula;
    const result = computeTransformedFormula(parentFormula, chainState.params);
    formula = 'g[x] = ' + result;
  }

  // Remove old DLs if canvas still exists
  if (_resultBoxDL && _resultBoxCanvas) {
    try {
      _resultBoxCanvas.model.removeElement(_resultBoxDL);
    } catch (e) {}
    _resultBoxDL = null;
  }
  if (_resultBoxPointRuleDL && _resultBoxCanvas) {
    try {
      _resultBoxCanvas.model.removeElement(_resultBoxPointRuleDL);
    } catch (e) {}
    _resultBoxPointRuleDL = null;
  }

  // Always show expanded height (consistent layout)
  box.classList.add('has-point-rule');

  const RESULT_DL_POS = { x: 140, y: 15 };
  const RULE_DL_POS = { x: 140, y: 110 };

  // Ensure label elements exist and update state
  function _ensureLabels() {
    if (!box.querySelector('.label-result')) {
      const lbl = document.createElement('span');
      lbl.className = 'result-box-label label-result';
      lbl.textContent = 'result';
      lbl.title = 'The transformed function g(x) after applying all transformations';
      box.appendChild(lbl);
    }
    let prLbl = box.querySelector('.label-point-rule');
    if (!prLbl) {
      prLbl = document.createElement('span');
      prLbl.className = 'result-box-label label-point-rule';
      prLbl.textContent = 'point rule';
      box.appendChild(prLbl);
    }
    prLbl.classList.toggle('missing', !pointRuleExpression);
  }

  // Create canvas on first use
  if (!_resultBoxCanvas) {
    box.innerHTML = '';
    _ensureLabels();
    const canvasId = 'result-box-canvas-' + Date.now();
    const div = document.createElement('div');
    div.id = canvasId;
    div.style.cssText =
      'width:100%; height:100%; position:absolute; top:0; left:0; background:transparent;';
    box.appendChild(div);
    setTimeout(() => {
      try {
        _resultBoxCanvas = createModalCanvas(canvasId);
        _resultBoxCanvas.settings.set('derivation_font_size', 32); // Slightly smaller for compact result box
        if (formula) {
          _resultBoxCanvas.model.createDL({ eq: formula, pos: RESULT_DL_POS }, (dl) => {
            _resultBoxDL = dl;
          });
        }
        if (pointRuleExpression) {
          _resultBoxCanvas.model.createDL(
            { eq: pointRuleExpression, pos: RULE_DL_POS },
            (dl) => {
              _resultBoxPointRuleDL = dl;
            }
          );
        }
      } catch (e) {
        console.warn('Could not create result box canvas:', e);
      }
    }, 50);
    return;
  }

  _ensureLabels();
  if (formula) {
    _resultBoxCanvas.model.createDL({ eq: formula, pos: RESULT_DL_POS }, (dl) => {
      _resultBoxDL = dl;
    });
  }
  if (pointRuleExpression) {
    _resultBoxCanvas.model.createDL(
      { eq: pointRuleExpression, pos: RULE_DL_POS },
      (dl) => {
        _resultBoxPointRuleDL = dl;
      }
    );
  }
}

const _helpOpts = { settingId: 'setting-auto-help' };

// ========== PARENT FUNCTIONS ==========
const PARENT_FUNCTIONS = {
  generic: {
    formula: 'f[x]',
    display: 'f(x)',
    name: 'Generic',
    eval: null,
    isGeneric: true,
  },
  constant: {
    formula: '1',
    display: '1',
    name: 'Constant',
    eval: (x) => 1,
    refX: 1,
    refY: 1,
  },
  linear: {
    formula: 'x',
    display: 'x',
    name: 'Linear',
    eval: (x) => x,
    refX: 1,
    refY: 1,
  },
  'absolute-value': {
    formula: '|x|',
    display: '|x|',
    name: 'Absolute Value',
    eval: (x) => Math.abs(x),
    refX: 1,
    refY: 1,
  },
  quadratic: {
    formula: 'x^2',
    display: 'x\u00B2',
    name: 'Quadratic',
    eval: (x) => x * x,
    refX: 1,
    refY: 1,
  },
  'square-root': {
    formula: 'sqrt(x)',
    display: '\u221Ax',
    name: 'Square Root',
    eval: (x) => (x >= 0 ? Math.sqrt(x) : NaN),
    refX: 1,
    refY: 1,
  },
  cubic: {
    formula: 'x^3',
    display: 'x\u00B3',
    name: 'Cubic',
    eval: (x) => x * x * x,
    refX: 1,
    refY: 1,
  },
  'cube-root': {
    formula: 'x^(1/3)',
    display: '\u221Bx',
    name: 'Cube Root',
    eval: (x) => Math.cbrt(x),
    refX: 1,
    refY: 1,
  },
  reciprocal: {
    formula: '1/x',
    display: '1/x',
    name: 'Reciprocal',
    eval: (x) => (x !== 0 ? 1 / x : NaN),
    refX: 1,
    refY: 1,
  },
  exponential: {
    formula: '2^x',
    display: '2\u02E3',
    name: 'Exponential',
    eval: (x) => Math.pow(2, x),
    refX: 1,
    refY: 2,
  },
  'natural-exponential': {
    formula: 'e^x',
    display: 'e\u02E3',
    name: 'Natural Exp',
    eval: (x) => Math.exp(x),
    refX: 1,
    refY: Math.E,
  },
  logarithmic: {
    formula: 'log(x)',
    display: 'log(x)',
    name: 'Logarithm',
    eval: (x) => (x > 0 ? Math.log10(x) : NaN),
    refX: 10,
    refY: 1,
  },
  'natural-log': {
    formula: 'ln(x)',
    display: 'ln(x)',
    name: 'Natural Log',
    eval: (x) => (x > 0 ? Math.log(x) : NaN),
    refX: Math.E,
    refY: 1,
  },
};

// ========== FORMULA COMPUTATION ==========
// Format a number for formula display - keep decimals as entered
function fmtNumForFormula(val) {
  return String(val);
}

function computeTransformedFormula(parentFormula, { a, b, h, k }) {
  // Format params as fractions when appropriate
  const hStr = fmtNumForFormula(Math.abs(h));
  const bStr = fmtNumForFormula(b);
  const aStr = fmtNumForFormula(a);
  const kStr = fmtNumForFormula(Math.abs(k));

  // Build the inner argument: b(x - h)
  // Use implicit multiplication where possible for cleaner display
  let innerArg;
  if (b === 1 && h === 0) {
    innerArg = 'x';
  } else if (b === 1) {
    innerArg = h > 0 ? `(x-${hStr})` : `(x+${hStr})`;
  } else if (h === 0) {
    innerArg = b === -1 ? '(-x)' : `${bStr}x`;
  } else {
    const hPart = h > 0 ? `(x-${hStr})` : `(x+${hStr})`;
    innerArg = b === -1 ? `(-${hPart})` : `${bStr}${hPart}`;
  }

  // Apply parent function to inner argument
  // Track if result is "self-grouping" (doesn't need parens when scaled)
  let fOfInner;
  let selfGrouping = false;

  if (parentFormula === 'x^2') {
    fOfInner = `${innerArg}^2`;
  } else if (parentFormula === 'x^3') {
    fOfInner = `${innerArg}^3`;
  } else if (parentFormula === 'x') {
    fOfInner = innerArg;
  } else if (parentFormula === '|x|') {
    // Absolute value bars provide grouping - strip redundant parens
    const cleanArg =
      innerArg.startsWith('(') && innerArg.endsWith(')')
        ? innerArg.slice(1, -1)
        : innerArg;
    fOfInner = `|${cleanArg}|`;
    selfGrouping = true;
  } else if (parentFormula === 'sqrt(x)') {
    // Function call provides grouping - strip redundant parens
    const cleanArg =
      innerArg.startsWith('(') && innerArg.endsWith(')')
        ? innerArg.slice(1, -1)
        : innerArg;
    fOfInner = `sqrt(${cleanArg})`;
    selfGrouping = true;
  } else if (parentFormula === 'x^(1/3)') {
    fOfInner = `${innerArg}^(1/3)`;
  } else if (parentFormula === '1/x') {
    // Denominator needs grouping, but strip redundant parens
    const cleanArg =
      innerArg.startsWith('(') && innerArg.endsWith(')')
        ? innerArg.slice(1, -1)
        : innerArg;
    fOfInner = `1/(${cleanArg})`;
    selfGrouping = true;
  } else if (parentFormula === '2^x') {
    // Exponent provides grouping
    const cleanArg =
      innerArg.startsWith('(') && innerArg.endsWith(')')
        ? innerArg.slice(1, -1)
        : innerArg;
    fOfInner = `2^(${cleanArg})`;
    selfGrouping = true;
  } else if (parentFormula === 'e^x') {
    const cleanArg =
      innerArg.startsWith('(') && innerArg.endsWith(')')
        ? innerArg.slice(1, -1)
        : innerArg;
    fOfInner = `e^(${cleanArg})`;
    selfGrouping = true;
  } else if (parentFormula === 'log(x)') {
    const cleanArg =
      innerArg.startsWith('(') && innerArg.endsWith(')')
        ? innerArg.slice(1, -1)
        : innerArg;
    fOfInner = `log(${cleanArg})`;
    selfGrouping = true;
  } else if (parentFormula === 'ln(x)') {
    const cleanArg =
      innerArg.startsWith('(') && innerArg.endsWith(')')
        ? innerArg.slice(1, -1)
        : innerArg;
    fOfInner = `ln(${cleanArg})`;
    selfGrouping = true;
  } else if (parentFormula === '1') {
    fOfInner = '1';
    selfGrouping = true;
  } else if (parentFormula === '"DATA"[x]') {
    // Data points: DATA[innerArg] - use bracket notation
    // Strip outer parens since brackets provide grouping
    const cleanArg =
      innerArg.startsWith('(') && innerArg.endsWith(')')
        ? innerArg.slice(1, -1)
        : innerArg;
    fOfInner = `"DATA"[${cleanArg}]`;
    selfGrouping = true;
  } else if (parentFormula.includes('[x]')) {
    // Generic function notation like f[x] -> f[innerArg]
    // Strip outer parens since brackets provide grouping
    const cleanArg =
      innerArg.startsWith('(') && innerArg.endsWith(')')
        ? innerArg.slice(1, -1)
        : innerArg;
    fOfInner = parentFormula.replace('[x]', `[${cleanArg}]`);
    selfGrouping = true;
  } else {
    // Generic replacement - avoid double-wrapping if innerArg is simple
    const needsParens = innerArg !== 'x' && !innerArg.startsWith('(');
    const wrappedArg = needsParens ? `(${innerArg})` : innerArg;
    fOfInner = parentFormula.replace(/x/g, wrappedArg);
  }

  // Apply vertical scaling
  // Use explicit * for JSXGraph compatibility (gmath handles both)
  let result;
  if (a === 1) {
    result = fOfInner;
  } else if (a === -1) {
    result = selfGrouping ? `-${fOfInner}` : `-(${fOfInner})`;
  } else {
    result = selfGrouping ? `${aStr}*${fOfInner}` : `${aStr}*(${fOfInner})`;
  }

  // Apply vertical shift
  if (k > 0) result = `${result}+${kStr}`;
  else if (k < 0) result = `${result}-${kStr}`;

  return result;
}

function buildFunctionApplication(parentType, innerArg) {
  switch (parentType) {
    case 'quadratic':
      return (
        '<span class="wizard-static">(</span>' +
        innerArg +
        '<span class="wizard-static">)\u00B2</span>'
      );
    case 'absolute-value':
      return (
        '<span class="wizard-static">|</span>' +
        innerArg +
        '<span class="wizard-static">|</span>'
      );
    case 'square-root':
      return (
        '<span class="wizard-static">\u221A(</span>' +
        innerArg +
        '<span class="wizard-static">)</span>'
      );
    case 'cubic':
      return (
        '<span class="wizard-static">(</span>' +
        innerArg +
        '<span class="wizard-static">)\u00B3</span>'
      );
    case 'cube-root':
      return (
        '<span class="wizard-static">\u221B(</span>' +
        innerArg +
        '<span class="wizard-static">)</span>'
      );
    case 'reciprocal':
      return (
        '<span class="wizard-static">1/(</span>' +
        innerArg +
        '<span class="wizard-static">)</span>'
      );
    case 'exponential':
      return (
        '<span class="wizard-static">2^(</span>' +
        innerArg +
        '<span class="wizard-static">)</span>'
      );
    case 'natural-exponential':
      return (
        '<span class="wizard-static">e^(</span>' +
        innerArg +
        '<span class="wizard-static">)</span>'
      );
    case 'logarithmic':
      return (
        '<span class="wizard-static">log(</span>' +
        innerArg +
        '<span class="wizard-static">)</span>'
      );
    case 'natural-log':
      return (
        '<span class="wizard-static">ln(</span>' +
        innerArg +
        '<span class="wizard-static">)</span>'
      );
    case 'polynomial':
      return (
        '<span class="wizard-static" style="font-style:italic;">f</span><span class="wizard-static">(</span>' +
        innerArg +
        '<span class="wizard-static">)</span>'
      );
    case 'constant':
      return '<span class="wizard-static">1</span>';
    case 'linear':
    default:
      return innerArg;
  }
}

// ========== ABSTRACT SUBSTITUTION BUILDER ==========
function fmtNum(v) {
  if (Number.isInteger(v)) return String(v);
  // Check for simple fractions (up to denominator 10)
  const absVal = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  for (let denom = 2; denom <= 10; denom++) {
    const numerator = absVal * denom;
    if (Math.abs(numerator - Math.round(numerator)) < 0.0001) {
      const num = Math.round(numerator);
      if (num !== denom) {
        // Don't show 2/2, just show 1
        return sign + num + '/' + denom;
      }
    }
  }
  return parseFloat(v.toFixed(4)).toString();
}

function buildAbstractSubstitution(type, value, prevLabel, newLabel) {
  switch (type) {
    case 'h-shift': {
      const av = fmtNum(Math.abs(value));
      const inner = value > 0 ? `x-${av}` : `x+${av}`;
      return `${newLabel}[x]=${prevLabel}[${inner}]`;
    }
    case 'v-shift': {
      const av = fmtNum(Math.abs(value));
      const op = value > 0 ? `+${av}` : `-${av}`;
      return `${newLabel}[x]=${prevLabel}[x]${op}`;
    }
    case 'v-stretch': {
      if (value === -1) return `${newLabel}[x]=-${prevLabel}[x]`;
      return `${newLabel}[x]=${fmtNum(value)}*${prevLabel}[x]`;
    }
    case 'h-stretch': {
      if (value === -1) return `${newLabel}[x]=${prevLabel}[-x]`;
      return `${newLabel}[x]=${prevLabel}[${fmtNum(value)}*x]`;
    }
    case 'reflect-x':
      return `${newLabel}[x]=-${prevLabel}[x]`;
    case 'reflect-y':
      return `${newLabel}[x]=${prevLabel}[-x]`;
    default:
      return `${newLabel}[x]=${prevLabel}[x]`;
  }
}

// ========== PRESETS ==========
const PAGE_PRESETS = {
  'preset-v-shift': {
    id: 'preset-v-shift',
    name: 'Shift Up',
    description: 'Quadratic shifted up 4 units',
    expressions: [
      {
        latex: 'y = x^2',
        graphFormula: 'y = x^2',
        type: 'parent-function',
        parentType: 'quadratic',
      },
    ],
    steps: [
      { type: 'v-shift', value: 4 }, // Shift up by 4
    ],
  },
  'preset-1': {
    id: 'preset-1',
    name: 'Shift Right',
    description: 'Quadratic shifted 3 units right',
    expressions: [
      {
        latex: 'y = x^2',
        graphFormula: 'y = x^2',
        type: 'parent-function',
        parentType: 'quadratic',
      },
    ],
    steps: [
      { type: 'h-shift', value: 3 }, // Shift right by 3
    ],
  },
  'preset-2': {
    id: 'preset-2',
    name: 'Reflect + Shift',
    description: 'Absolute value reflected and shifted up 4',
    expressions: [
      {
        latex: 'y = |x|',
        graphFormula: 'y = abs(x)',
        type: 'parent-function',
        parentType: 'absolute-value',
      },
    ],
    steps: [
      { type: 'reflect-x', value: null }, // Reflect across x-axis
      { type: 'v-shift', value: 4 }, // Shift up by 4
    ],
  },
  'preset-3': {
    id: 'preset-3',
    name: 'Stretch + Shift',
    description: 'Quadratic stretched \u00d72, shifted down 3',
    expressions: [
      {
        latex: 'y = x^2',
        graphFormula: 'y = x^2',
        type: 'parent-function',
        parentType: 'quadratic',
      },
    ],
    steps: [
      { type: 'v-stretch', value: 2 }, // Vertical stretch by 2
      { type: 'v-shift', value: -3 }, // Shift down by 3
    ],
  },
};

// ========== SIDEBAR CARD DEFINITIONS ==========
const TRANSFORM_CARDS = [
  {
    id: 'card-parent',
    type: 'select-parent',
    label: 'Select parent f(x)',
    category: 'parent',
    icon: 'f',
    requiresParent: false,
  },
  null, // divider
  {
    id: 'card-h-shift',
    type: 'h-shift',
    label: 'Horizontal Shift',
    category: 'shift',
    icon: 'h',
    requiresParent: true,
  },
  {
    id: 'card-v-shift',
    type: 'v-shift',
    label: 'Vertical Shift',
    category: 'shift',
    icon: 'k',
    requiresParent: true,
  },
  null, // divider
  {
    id: 'card-reflect-x',
    type: 'reflect-x',
    label: 'Reflect x-axis',
    category: 'reflect',
    icon: '\u2195',
    requiresParent: true,
  },
  {
    id: 'card-reflect-y',
    type: 'reflect-y',
    label: 'Reflect y-axis',
    category: 'reflect',
    icon: '\u2194',
    requiresParent: true,
  },
  null, // divider
  {
    id: 'card-v-stretch',
    type: 'v-stretch',
    label: 'Vertical Stretch',
    category: 'stretch',
    icon: 'a',
    requiresParent: true,
  },
  {
    id: 'card-h-stretch',
    type: 'h-stretch',
    label: 'Horizontal Stretch',
    category: 'stretch',
    icon: 'b',
    requiresParent: true,
  },
];

// Create page header and settings panel immediately (before DOMContentLoaded)
// so they exist when initCanvas() runs and creates PresetManager.
GuidedTopics.createPageHeader({
  title: 'Function Transformations: Reflections, Stretches, Translations',
  subject: 'functions',
  guideUrl: 'function-transformations-rst-guide.html',
  // Presets now rendered as dropdown (see setupPresetDropdown below)
});
GuidedTopics.createSettingsPanel({
  items: [
    {
      type: 'checkbox',
      id: 'setting-auto-graph',
      label: 'Automatically graph parent function',
      checked: true,
      title: 'Graph the parent function as soon as it is placed on the canvas.',
    },
    {
      type: 'checkbox',
      id: 'setting-auto-graph-child',
      label: 'Automatically graph transformed formula',
      checked: true,
      title: 'Graph the transformed function and animate the transition from the parent.',
    },
    { type: 'equation-mode' },
    {
      type: 'checkbox',
      id: 'setting-integer-shifts',
      label: 'Restrict shifts (h, k) to integers',
      checked: false,
      title: 'Snap horizontal and vertical shift values to whole numbers only.',
    },
    {
      type: 'checkbox',
      id: 'setting-auto-help',
      label: 'Automatic Help',
      checked: true,
      title: 'Show contextual hints when students seem stuck or go off-path.',
    },
    {
      type: 'checkbox',
      id: 'setting-allow-interactions',
      label: 'Freeform equation interactions',
      title:
        'Turning on this option allows you to use the full power of Graspable Math anywhere.',
    },
    { type: 'section-header', label: 'Terminology' },
    {
      type: 'radio-group',
      name: 'naming-mode',
      label: 'Label transformed functions as...',
      options: [
        {
          id: 'naming-subscript',
          value: 'subscript',
          label: 'f\u2081, f\u2082, f\u2083 (subscripts)',
          checked: true,
        },
        { id: 'naming-letter', value: 'letter', label: 'g, h, j, k (distinct letters)' },
      ],
    },
    {
      type: 'radio-group',
      name: 'term-shift',
      label: 'Call horizontal/vertical moves...',
      options: [
        { id: 'term-shift-shift', value: 'shift', label: 'Shifts', checked: true },
        { id: 'term-shift-translation', value: 'translation', label: 'Translations' },
      ],
    },
    {
      type: 'radio-group',
      name: 'term-scale',
      label: 'Call scaling transformations...',
      options: [
        {
          id: 'term-scale-stretch',
          value: 'stretch',
          label: 'Stretches/Compressions',
          checked: true,
        },
        { id: 'term-scale-dilation', value: 'dilation', label: 'Dilations' },
      ],
    },
  ],
});

// ========== DOM READY ==========
document.addEventListener('DOMContentLoaded', () => {
  GuidedTopics.initInfoMessage();
  GuidedTopics.initSettingsPanel();

  ({ observationStore, observationMatcher, thinkingPanel } =
    GuidedTopics.initThinkingPanel({
      container: document.body,
      triggerId: 'thinking-panel-trigger',
      tuples: GuidedTopics.functionTransformationsObservationTuples,
      thinkingConfig: GuidedTopics.functionTransformationsThinkingConfig,
    }));

  // Render sidebar
  renderTransformSidebar();

  // Terminology radio button handlers - re-render sidebar when changed
  [
    'term-shift-shift',
    'term-shift-translation',
    'term-scale-stretch',
    'term-scale-dilation',
  ].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.addEventListener('change', () => renderTransformSidebar());
  });

  // Function naming mode handler - update existing labels when toggled
  ['naming-subscript', 'naming-letter'].forEach((id) => {
    const el = document.getElementById(id);
    if (el)
      el.addEventListener('change', () => {
        const useLetters = isLetterNamingMode();
        chainDLs.forEach((entry, idx) => {
          if (entry.type === 'chain-step' && entry.labelButton) {
            const newLabel = getStepDisplayLabel(idx);
            entry.labelButton.textContent = newLabel;
            // Toggle subscript CSS class based on naming mode
            entry.labelButton.classList.toggle('subscript-label', !useLetters);
            entry.label = newLabel;
          }
        });
      });
  });

  // Parent function wizard is instantiated below (near other wizards)
});

// ========== SETTINGS (shared utilities) ==========
const {
  toggleCanvasInteractions,
  applyEquationInteractionsMode,
  getCurrentEquationMode,
  getDerivationSettings,
} = GuidedTopics;

// ========== CANVAS INIT ==========
function initCanvas() {
  canvas = GuidedTopics.initGuidedCanvas();

  // Scale down expressions to 55% of default size (50 * 0.55 ≈ 28)
  canvas.settings.set('derivation_font_size', MAX_FONT_SIZE);

  tutorialManager = new GuidedTopics.TutorialManager(canvas, {
    settingId: 'setting-auto-help',
  });

  // Initialize expression system (label buttons + form dropdown management)
  try {
    exprSystem = GuidedTopics.initExpressionSystem({
      canvas,
      containerEl: document.getElementById('gm-page-container'),
      expressions,
      formTypes: ['original', 'rewrite'],
      observationStore,
      getDerivationSettings: () => getDerivationSettings(),
      onLabelClick: (expr) => {
        const settingId =
          expr.type === 'parent-function'
            ? 'setting-auto-graph'
            : 'setting-auto-graph-child';
        const graphingEnabled = document.getElementById(settingId).checked;
        if (graphingEnabled && !isExpressionGraphed(expr)) graphSingleExpression(expr);
      },
      onDerivationUpdated: () => {
        if (exprSystem) exprSystem.syncEntities();
      },
    });
    formManager = exprSystem.formManager;
  } catch (e) {
    console.warn('ExpressionSystem not available:', e);
  }

  // Initialize TransformFunctionFlow (rewrite/simplify workflow)
  try {
    transformFunctionFlow = new GuidedTopics.Workflows.TransformFunctionFlow({
      getExpressions: () =>
        expressions.filter(
          (e) => e.type === 'parent-function' || e.type === 'transformed-function'
        ),
      showInfoMessage,
      latexToUnicode: GuidedTopics.latexToUnicode,
      getDerivationSettings: () => getDerivationSettings(),
      applyEquationMode: () => applyEquationInteractionsMode(getCurrentEquationMode()),
      emptyMessage: 'Select a parent function first.',
      detectForms: [
        'vertex',
        'factored-quadratic',
        'expanded-quadratic',
        'slope-intercept',
        'point-slope',
        'standard',
      ],
      onResult: (expr, formName, resultAscii) => {
        if (formManager) {
          formManager.addForm(expr, formName, resultAscii, resultAscii);
          // Switch to the newly added form so the explore page updates
          const newIndex = expr.forms.findIndex((f) => f.name === formName);
          if (newIndex !== -1) formManager.setActiveForm(expr, newIndex);
        }
      },
    });
  } catch (e) {
    console.warn('TransformFunctionFlow not available:', e);
  }

  try {
    hintLookup = GuidedTopics.createHintLookup(GuidedTopics.allHintTuples);
    GuidedTopics.initMinimalGuardrails(canvas, hintLookup);

    // DevPanel for hint authoring (?hintDev=1)
    if (GuidedTopics.DevPanel?.isDevMode()) {
      new GuidedTopics.DevPanel({
        canvas: canvas,
        hintLookup: hintLookup,
        affordance: 'modal-transform',
      });
    }
  } catch (e) {
    console.warn('Hint lookup not available:', e);
  }

  presetManager = new GuidedTopics.PresetManager(canvas, {
    expressionTracker: expressions,
    createLabelButton: (exprData) => {
      if (exprData.type === 'parent-function') {
        const currentLabel = 'f';
        const color = { r: 108, g: 117, b: 125 };
        exprData.color = color;
        exprData.label = currentLabel;

        if (exprData.parentType && PARENT_FUNCTIONS[exprData.parentType]) {
          parentFunctionType = exprData.parentType;
          parentFunction = PARENT_FUNCTIONS[exprData.parentType];
        }
        parentExprData = exprData;
        updateSidebarCardStates();

        return exprSystem.createLabelButton(
          currentLabel,
          exprData.labelX,
          exprData.labelY,
          color
        );
      } else {
        // Transformed functions from presets
        const currentDisplayLabel = getStepDisplayLabel(chainState.stepIndex);
        chainState.stepIndex++;
        const color =
          GuidedTopics.DEFAULT_COLOR_PALETTE[
            colorIndex % GuidedTopics.DEFAULT_COLOR_PALETTE.length
          ];
        colorIndex++;
        exprData.color = color;
        exprData.label = currentDisplayLabel;
        const btn = exprSystem.createLabelButton(
          currentDisplayLabel,
          exprData.labelX,
          exprData.labelY,
          color
        );
        if (!isLetterNamingMode()) btn.classList.add('subscript-label');
        return btn;
      }
    },
    colorPalette: GuidedTopics.DEFAULT_COLOR_PALETTE,
    onExpressionCreated: (expr) => {
      colorIndex = expressions.filter((e) => e.type === 'transformed-function').length;
      if (exprSystem) exprSystem.registerExpression(expr);
    },
    onCanvasCleared: () => {
      if (typeof cancelMorphAnimation === 'function') cancelMorphAnimation();
      document
        .querySelectorAll('#gm-page-container .step-reason')
        .forEach((el) => el.remove());
      colorIndex = 0;
      parentFunction = null;
      parentFunctionType = null;
      parentExprData = null;
      chainState.params = { a: 1, b: 1, h: 0, k: 0 };
      chainState.history = [];
      chainState.stepIndex = 0;
      chainDLs = [];
      if (graphingManagerParent) graphingManagerParent.reset();
      if (transformFunctionFlow) transformFunctionFlow.resetCounter();
      if (exprSystem) exprSystem.reset();
      updateResultGraph(null);
      updateSidebarCardStates();
      onChainChanged();
      startChooser?.show();
    },
    afterPresetLoaded: () => {
      startChooser?.hide();
      if (exprSystem) exprSystem.syncEntities();
      setTimeout(() => {
        initGraphPanels();
        const autoGraphParent = document.getElementById('setting-auto-graph').checked;
        expressions.forEach((expr) => {
          if (isExpressionGraphed(expr)) return;
          if (expr.type === 'parent-function' && autoGraphParent)
            graphSingleExpression(expr);
        });
      }, 200);
    },
    expressionSpacing: 120,
    startY: 30,
  });

  // Create StartChooser — unified entry point for presets and parent function selection
  const { StartChooser } = GuidedTopics;
  if (StartChooser) {
    // Build parent function cards with SVG graph sketch icons and SVG math labels
    // (matching function-combination's visual style)
    const icons = GuidedTopics.Wizards?.PARENT_FUNCTION_ICONS || {};

    // SVG math labels — Kalam font to match GM canvas rendering
    const F = 'Kalam, Georgia, serif';
    const C = 'currentColor';
    const svgMath = (vw, content) =>
      `<svg viewBox="0 0 ${vw} 28" class="chooser-math-svg" fill="none"><style>text{font-family:${F};fill:${C}}</style>${content}</svg>`;

    const MATH_LABELS = {
      constant: svgMath(
        16,
        `<text x="8" y="21" font-size="18" text-anchor="middle" font-style="italic">1</text>`
      ),
      linear: svgMath(
        16,
        `<text x="8" y="21" font-size="18" text-anchor="middle" font-style="italic">x</text>`
      ),
      'absolute-value': svgMath(
        32,
        `<text x="2" y="21" font-size="18">|</text><text x="14" y="21" font-size="18" font-style="italic">x</text><text x="25" y="21" font-size="18">|</text>`
      ),
      quadratic: svgMath(
        28,
        `<text x="2" y="21" font-size="18" font-style="italic">x</text><text x="15" y="14" font-size="12">2</text>`
      ),
      'square-root': svgMath(
        34,
        `<path d="M1 18 L5 20 L12 4 L32 4" stroke="${C}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><text x="18" y="22" font-size="17" font-style="italic">x</text>`
      ),
      cubic: svgMath(
        28,
        `<text x="2" y="21" font-size="18" font-style="italic">x</text><text x="15" y="14" font-size="12">3</text>`
      ),
      'cube-root': svgMath(
        40,
        `<text x="1" y="12" font-size="10">3</text><path d="M7 18 L11 20 L18 4 L38 4" stroke="${C}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><text x="24" y="22" font-size="17" font-style="italic">x</text>`
      ),
      reciprocal: svgMath(
        22,
        `<text x="11" y="12" font-size="14" text-anchor="middle">1</text><line x1="2" y1="14.5" x2="20" y2="14.5" stroke="${C}" stroke-width="1.3"/><text x="11" y="26" font-size="14" text-anchor="middle" font-style="italic">x</text>`
      ),
      exponential: svgMath(
        28,
        `<text x="2" y="21" font-size="18">2</text><text x="15" y="13" font-size="12" font-style="italic">x</text>`
      ),
      'natural-exponential': svgMath(
        26,
        `<text x="2" y="21" font-size="18" font-style="italic">e</text><text x="14" y="13" font-size="12" font-style="italic">x</text>`
      ),
      logarithmic: svgMath(
        52,
        `<text x="2" y="21" font-size="16">log(</text><text x="35" y="21" font-size="16" font-style="italic">x</text><text x="46" y="21" font-size="16">)</text>`
      ),
      'natural-log': svgMath(
        42,
        `<text x="2" y="21" font-size="16">ln(</text><text x="26" y="21" font-size="16" font-style="italic">x</text><text x="37" y="21" font-size="16">)</text>`
      ),
    };

    // All parent functions that should appear in the chooser (skip generic)
    const ALL_PARENTS = Object.keys(PARENT_FUNCTIONS).filter((k) => k !== 'generic');
    const parentPresets = {};
    ALL_PARENTS.forEach((type) => {
      const pf = PARENT_FUNCTIONS[type];
      if (!pf) return;
      const id = `parent-${type}`;
      parentPresets[id] = {
        id,
        mathHtml: MATH_LABELS[type],
        name: pf.display,
        icon: icons[type] || '',
        _parentType: type,
      };
    });

    // Merge parent functions + example presets into one keyed object
    const allPresets = { ...parentPresets, ...PAGE_PRESETS };

    startChooser = new StartChooser({
      presets: allPresets,
      container: document.querySelector('.canvas-wrapper'),
      title: 'Choose a transformation to explore',
      hideCreateOwn: false,
      createOwnLabel: 'More Functions\u2026',
      wideGrid: true,
      categories: [
        {
          label: 'Select a parent function',
          presetIds: ALL_PARENTS.map((t) => `parent-${t}`),
        },
        { label: 'Or start from an example', presetIds: Object.keys(PAGE_PRESETS) },
      ],
      onPresetSelect: async (presetId) => {
        // Route parent function cards to selectParentFunction()
        const parentPreset = parentPresets[presetId];
        if (parentPreset) {
          selectParentFunction(parentPreset._parentType);
          return;
        }
        // Regular preset
        const preset = PAGE_PRESETS[presetId];
        if (preset) {
          await presetManager.loadPreset(preset);
          if (preset.steps?.length) {
            await new Promise((r) => setTimeout(r, 300));
            applyPresetSteps(preset.steps);
          }
        }
      },
      onCreateOwn: () => openParentFunctionOverlay(),
    });
    startChooser.show();
  }

  setTimeout(() => applyEquationInteractionsMode('fast'), 100);
  setTimeout(() => initGraphPanels(), 500);
}

/**
 * Programmatically apply a series of transformation steps.
 * @param {Array} steps - Array of { type, value } objects
 */
function applyPresetSteps(steps) {
  if (!parentFunctionType) {
    console.warn('Cannot apply preset steps: no parent function');
    return;
  }

  for (const step of steps) {
    applyTransformation(step.type, step.value);
    addChainStepDL();
  }

  onChainChanged();
}

// ========== ZOOM SYNC ==========
let _syncingZoom = false;

function setupZoomSync(managerA, managerB) {
  if (!managerA?.board || !managerB?.board) return;
  let lastBB = null;

  managerA.board.on('update', () => {
    if (_syncingZoom) return;
    const bb = managerA.board.getBoundingBox();
    const bbStr = bb.join(',');
    if (bbStr === lastBB) return;
    lastBB = bbStr;
    _syncingZoom = true;
    managerB.board.setBoundingBox(bb, false);
    requestAnimationFrame(() => {
      _syncingZoom = false;
    });
  });

  managerB.board.on('update', () => {
    if (_syncingZoom) return;
    const bb = managerB.board.getBoundingBox();
    const bbStr = bb.join(',');
    if (bbStr === lastBB) return;
    lastBB = bbStr;
    _syncingZoom = true;
    managerA.board.setBoundingBox(bb, false);
    requestAnimationFrame(() => {
      _syncingZoom = false;
    });
  });
}

// ========== GRAPH PANELS (dual vertical: parent + result) ==========
function initGraphPanels() {
  if (typeof cancelMorphAnimation === 'function') cancelMorphAnimation();
  if (jsxManagerParent) jsxManagerParent.destroy();
  if (jsxManagerResult) jsxManagerResult.destroy();
  jsxManagerParent = null;
  jsxManagerResult = null;
  graphingManagerParent = null;

  // Parent panel (top)
  jsxManagerParent = new GuidedTopics.JSXGraphManager({
    containerId: 'jsxgraph-container-parent',
    backgroundColor: '#eef4fc',
    boundingBox: [-12, 14, 12, -6],
  });
  jsxManagerParent.init();

  graphingManagerParent = new GuidedTopics.JSXGraphGraphingManager({
    jsxGraphManager: jsxManagerParent,
    getExpressions: () => expressions,
    graphableTypes: ['parent-function'],
    colorPalette: GuidedTopics.DEFAULT_COLOR_PALETTE,
  });

  // Result panel (bottom)
  jsxManagerResult = new GuidedTopics.JSXGraphManager({
    containerId: 'jsxgraph-container-result',
    backgroundColor: '#f0ebff',
    boundingBox: [-12, 14, 12, -6],
  });
  jsxManagerResult.init();

  setupZoomSync(jsxManagerParent, jsxManagerResult);
}

// ========== RESULT GRAPH UPDATE ==========
let _currentResultGraphName = null;
let _parentRefGraphed = false;

function updateResultGraph(graphFormula) {
  if (!jsxManagerResult?.isReady) return;

  // Remove previous result curve
  if (_currentResultGraphName) {
    try {
      jsxManagerResult.removeElement(_currentResultGraphName);
    } catch (e) {}
    _currentResultGraphName = null;
  }

  const funcData = PARENT_FUNCTIONS[parentFunctionType];
  const isDiscreteData = funcData?.isDiscreteData && funcData.points;

  if ((!graphFormula && !isDiscreteData) || chainState.history.length === 0) {
    // Remove parent reference too if clearing
    if (_parentRefGraphed) {
      try {
        jsxManagerResult.removeElement('parent_ref');
      } catch (e) {}
      _parentRefGraphed = false;
    }
    return;
  }

  // Show parent as faint reference on result graph
  if (!_parentRefGraphed && parentFunctionType) {
    if (isDiscreteData) {
      // Data points: show faint piecewise reference
      try {
        jsxManagerResult.graphPiecewiseCurve(funcData.points, {
          name: 'parent_ref',
          color: '#9ca3af',
          strokeWidth: 1.5,
          dash: 2,
          showPoints: false,
        });
        _parentRefGraphed = true;
      } catch (e) {
        console.warn('Could not graph parent reference:', e);
      }
    } else {
      // Standard formula reference
      const parentFormulaStr = 'y = ' + funcData.formula;
      try {
        jsxManagerResult.graphFormula(parentFormulaStr, {
          name: 'parent_ref',
          color: '#9ca3af',
          strokeWidth: 1.5,
          dash: 2,
          withLabel: false,
        });
        _parentRefGraphed = true;
      } catch (e) {
        console.warn('Could not graph parent reference:', e);
      }
    }
  }

  // Graph the current result
  const stepIdx = chainState.history.length - 1;
  const color =
    GuidedTopics.DEFAULT_COLOR_PALETTE[
      stepIdx % GuidedTopics.DEFAULT_COLOR_PALETTE.length
    ];
  const cssColor = `rgb(${color.r}, ${color.g}, ${color.b})`;
  const name = 'result_' + chainState.stepIndex;

  if (isDiscreteData) {
    // Transform each data point: (x, y) → (x/b + h, a*y + k)
    const { a, b, h, k } = chainState.params;
    const transformedPoints = funcData.points.map((p) => ({
      x: p.x / b + h,
      y: a * p.y + k,
    }));
    try {
      const result = jsxManagerResult.graphPiecewiseCurve(transformedPoints, {
        name: name,
        color: cssColor,
        strokeWidth: 2.5,
        showPoints: true,
        withLabel: false,
      });
      if (result) _currentResultGraphName = result.name || name;
    } catch (e) {
      console.warn('Could not graph result:', e);
    }
  } else {
    try {
      const result = jsxManagerResult.graphFormula(graphFormula, {
        name: name,
        color: cssColor,
        strokeWidth: 2.5,
        withLabel: false,
      });
      if (result) _currentResultGraphName = result.name || name;
    } catch (e) {
      console.warn('Could not graph result:', e);
    }
  }
}

// ========== LABELS & TRACKING ==========
function isExpressionGraphed(expr) {
  return graphingManagerParent?.isGraphed(expr) || false;
}

function graphSingleExpression(expr) {
  if (expr?.type === 'parent-function') {
    if (!jsxManagerParent?.isReady) {
      showInfoMessage('Graph is still loading. Please wait a moment and try again.');
      return;
    }

    const funcData = PARENT_FUNCTIONS[parentFunctionType];
    if (funcData?.isDiscreteData && funcData.points) {
      // Data points: use piecewise curve method
      const color = expr.color || graphingManagerParent.getNextColor();
      jsxManagerParent.graphPiecewiseCurve(funcData.points, {
        name: expr.label,
        color: rgbToCss(color),
        showPoints: true,
        strokeWidth: 2,
      });
      graphingManagerParent.markAsGraphed(expr);
      if (expr.labelButton) expr.labelButton.classList.add('graphed');
    } else {
      // Standard formula-based graphing
      graphingManagerParent.graphExpression(expr);
    }
  }
  // Transformed functions are handled via updateResultGraph()
}

const rgbToCss = GuidedTopics.rgbToCss;

// ========== OVERLAY / INFO (shared utilities) ==========
const { openOverlay, closeOverlay, showInfoMessage, closeInfoMessage } = GuidedTopics;

// ========== SELECT PARENT FUNCTION ==========
// ParentFunctionWizard instance (created after openComplexParentWizard is defined)
let parentFunctionWizard;

function openParentFunctionOverlay() {
  // Strip 'complex-' prefix for card matching (e.g. 'complex-quadratic' → 'quadratic')
  const cardType = parentFunctionType?.startsWith('complex-')
    ? parentFunctionType.replace('complex-', '')
    : parentFunctionType;

  parentFunctionWizard.mode = parentSelectMode;
  parentFunctionWizard.selectedType = cardType;
  parentFunctionWizard.open();
}

function selectParentFunction(type) {
  if (!PARENT_FUNCTIONS[type]) {
    showInfoMessage('Unknown parent function type.');
    return;
  }

  // Exit graph transform mode when parent changes
  if (graphTransformState.active) {
    exitGraphTransformMode();
  }

  const funcData = PARENT_FUNCTIONS[type];
  parentFunctionType = type;
  parentFunction = funcData;

  if (!canvas || !canvas.model) {
    showInfoMessage('Please wait for the canvas to load.');
    return;
  }

  // Remove existing parent function if present
  if (parentExprData) {
    if (parentExprData.labelButton) parentExprData.labelButton.remove();
    if (parentExprData.reasonEl) parentExprData.reasonEl.remove();
    try {
      canvas.model.removeElement(parentExprData.derivation);
    } catch (e) {}
    const idx = expressions.indexOf(parentExprData);
    if (idx !== -1) expressions.splice(idx, 1);
    if (graphingManagerParent) graphingManagerParent.reset();
    parentExprData = null;
  }

  // Reset chain when parent changes
  if (chainState.history.length > 0) {
    chainState.params = { a: 1, b: 1, h: 0, k: 0 };
    chainState.history = [];
    chainState.stepIndex = 0;
    removeAllChainDLs();
  }

  // Reset result graph and result function box
  updateResultGraph(null);
  _parentRefGraphed = false;
  updateResultFunctionBox();
  updateParamDisplay();

  const displayFormula = 'f[x] = ' + funcData.formula;
  const graphFormula = 'y = ' + funcData.formula;
  const color = { r: 108, g: 117, b: 125 };

  canvas.model.createDL(
    {
      eq: displayFormula,
      pos: { x: 80, y: 30 },
    },
    (createdDL) => {
      GuidedTopics.animateExpressionEntrance?.(createdDL);
      setTimeout(() => {
        fitExpressionToWidth(createdDL, 80, 30); // Scale down if too wide
        const labelX = createdDL.pos.x - 35;
        const labelY = createdDL.pos.y + (createdDL.size?.height || 40) / 2 - 17.5;
        const labelBtn = exprSystem.createLabelButton('f', labelX, labelY, color);
        const reasonEl = createReasonOverlay('Parent function', labelY);
        const exprData = {
          label: 'f',
          derivation: createdDL,
          row: createdDL.getLastRow(),
          expression: displayFormula,
          graphFormula: graphFormula,
          type: 'parent-function',
          parentType: type,
          labelButton: labelBtn,
          reasonEl: reasonEl,
          color: color,
        };
        expressions.push(exprData);
        parentExprData = exprData;
        if (exprSystem) exprSystem.registerExpression(exprData);

        exprSystem.syncEntities();
        updateSidebarCardStates();
        onChainChanged();
        if (document.getElementById('setting-auto-graph').checked) {
          graphSingleExpression(exprData);
        }
      }, 200);
    }
  );
}

// ========== POLYNOMIAL WIZARD ==========
const PolynomialWizard = GuidedTopics.Wizards.PolynomialWizard;

const polynomialWizard = new PolynomialWizard({
  onCreatePolynomial: (effectiveCoefs, degree) => {
    const formula = PolynomialWizard.buildFormula(effectiveCoefs, degree);
    const display = PolynomialWizard.buildDisplay(effectiveCoefs, degree);
    const evalFn = PolynomialWizard.buildEval(effectiveCoefs, degree);

    PARENT_FUNCTIONS['polynomial'] = {
      formula: formula,
      display: display,
      name: 'Polynomial',
      eval: evalFn,
      degree: degree,
      coefs: effectiveCoefs,
      refX: 1,
      refY: evalFn(1),
    };

    selectParentFunction('polynomial');
  },
});

function openPolynomialWizard() {
  polynomialWizard.open();
}

// ========== SIMPLE POLYNOMIAL (degree-only, all coefs = 1) ==========
const polynomialWizardSimple = new PolynomialWizard({
  onCreatePolynomial: (effectiveCoefs, degree) => {
    const formula = PolynomialWizard.buildFormula(effectiveCoefs, degree);
    const display = PolynomialWizard.buildDisplay(effectiveCoefs, degree);
    const evalFn = PolynomialWizard.buildEval(effectiveCoefs, degree);

    PARENT_FUNCTIONS['polynomial'] = {
      formula: formula,
      display: display,
      name: 'Polynomial',
      eval: evalFn,
      degree: degree,
      coefs: effectiveCoefs,
      refX: 1,
      refY: evalFn(1),
    };

    selectParentFunction('polynomial');
  },
});

// Override continueToCoefficients to skip coefficient entry:
// all coefficients default to 1, immediately create the polynomial.
polynomialWizardSimple.continueToCoefficients = function () {
  if (this._degree === null) return;
  const allOnesCoefs = Array.from({ length: this._degree + 1 }, () => 1);
  this.close();
  this._onCreatePolynomial(allOnesCoefs, this._degree);
};

function openSimplePolynomialWizard() {
  polynomialWizardSimple.open();
}

// ========== COMPLEX PARENT WIZARD (a, b, h, k) ==========
const ComplexParentWizard = GuidedTopics.Wizards.ComplexParentWizard;

// Helper to get shift type from settings
function getShiftType() {
  const checkbox = document.getElementById('setting-integer-shifts');
  return checkbox && checkbox.checked ? 'integer' : 'decimal';
}

const complexParentWizard = new ComplexParentWizard({
  buildFunctionApplication: buildFunctionApplication,
  getShiftType: getShiftType,
  onConfirm: (params, baseType) => {
    const baseFunc = PARENT_FUNCTIONS[baseType];
    if (!baseFunc) return;

    // Compute baked-in formula from params
    const formula = computeTransformedFormula(baseFunc.formula, params);

    // Build display string
    const display = buildComplexDisplay(baseType, params);

    // Build eval function by composing base eval with transforms
    const baseEval = baseFunc.eval;
    const evalFn = (x) => {
      const inner = params.b * (x - params.h);
      return params.a * baseEval(inner) + params.k;
    };

    const key = 'complex-' + baseType;
    PARENT_FUNCTIONS[key] = {
      formula: formula,
      display: display,
      name: baseFunc.name + ' (complex)',
      eval: evalFn,
      complexParams: params,
      baseType: baseType,
      refX: 1,
      refY: evalFn(1),
    };

    selectParentFunction(key);
  },
});

function openComplexParentWizard(type) {
  const funcData = PARENT_FUNCTIONS[type];
  complexParentWizard.open(type, funcData ? funcData.name : type);
}

// ========== DATA POINTS WIZARD ==========
const DataPointsWizard = GuidedTopics.Wizards.DataPointsWizard;

const dataPointsWizard = new DataPointsWizard({
  onCreateDataPoints: (points, datasetId) => {
    // Create parent function entry with discrete data
    const dpEval = DataPointsWizard.buildEval(points);
    PARENT_FUNCTIONS['data-points'] = {
      formula: '"DATA"[x]',
      display: `Data ${datasetId}`,
      name: 'Data Points',
      points: points,
      eval: dpEval,
      isDiscreteData: true,
      refX: points[1]?.x ?? 1,
      refY: points[1]?.y ?? 1,
    };
    selectParentFunction('data-points');
  },
});

// ========== PARENT FUNCTION WIZARD ==========
const ParentFunctionWizard = GuidedTopics.Wizards.ParentFunctionWizard;

parentFunctionWizard = new ParentFunctionWizard({
  excludeTypes: ['sine', 'cosine', 'gaussian'],
  onSelect: (type, mode) => {
    parentSelectMode = mode; // Track mode for next open
    if (mode === 'simple') {
      selectParentFunction(type);
    } else {
      openComplexParentWizard(type);
    }
  },
  onSelectPolynomial: (mode) => {
    parentSelectMode = mode;
    if (mode === 'simple') {
      openSimplePolynomialWizard();
    } else {
      openPolynomialWizard();
    }
  },
  onSelectDataPoints: (mode) => {
    parentSelectMode = mode;
    dataPointsWizard.open();
  },
  onSelectGeneric: () => {
    // Generic has no complex form - just select directly
    selectParentFunction('generic');
  },
});

/**
 * Build a display string for a complex parent function.
 * Uses Unicode superscripts and symbols for readability.
 */
function buildComplexDisplay(parentType, { a, b, h, k }) {
  // Build inner arg display
  let inner;
  if (b === 1 && h === 0) {
    inner = 'x';
  } else if (b === 1) {
    inner = h > 0 ? `(x\u2212${h})` : `(x+${Math.abs(h)})`;
  } else if (h === 0) {
    inner = b === -1 ? '(\u2212x)' : `(${b}x)`;
  } else {
    const hPart = h > 0 ? `x\u2212${h}` : `x+${Math.abs(h)}`;
    inner = b === -1 ? `(\u2212(${hPart}))` : `(${b}(${hPart}))`;
  }

  // Apply function notation
  let fInner;
  switch (parentType) {
    case 'quadratic':
      fInner = `(${inner})\u00B2`;
      break;
    case 'absolute-value':
      fInner = `|${inner}|`;
      break;
    case 'square-root':
      fInner = `\u221A${inner}`;
      break;
    case 'cubic':
      fInner = `(${inner})\u00B3`;
      break;
    case 'cube-root':
      fInner = `\u221B${inner}`;
      break;
    case 'reciprocal':
      fInner = `1/${inner}`;
      break;
    case 'constant':
      fInner = '1';
      break;
    case 'linear':
    default:
      fInner = inner;
      break;
  }

  // Apply vertical scale
  let result;
  if (a === 1) result = fInner;
  else if (a === -1) result = `\u2212${fInner}`;
  else result = `${a}\u00B7${fInner}`;

  // Apply vertical shift
  if (k > 0) result += `+${k}`;
  else if (k < 0) result += `\u2212${Math.abs(k)}`;

  return result;
}

// ========== SIDEBAR CARD RENDERING ==========
function renderTransformSidebar() {
  const sidebar = document.getElementById('transform-sidebar');
  sidebar.innerHTML = '';

  for (const cardDef of TRANSFORM_CARDS) {
    if (cardDef === null) {
      const div = document.createElement('div');
      div.className = 'sidebar-divider';
      sidebar.appendChild(div);
      continue;
    }

    const btn = document.createElement('button');
    btn.className = `sidebar-card card-${cardDef.category}`;
    btn.id = cardDef.id;
    const isDisabled = cardDef.requiresParent && !parentFunctionType;
    btn.disabled = isDisabled;
    if (isDisabled) btn.title = 'Select a parent function first';
    // Use dynamic terminology for transform cards, static label for parent
    const label =
      cardDef.type === 'select-parent' ? cardDef.label : getTransformLabel(cardDef.type);
    btn.innerHTML = `<div class="card-icon">${cardDef.icon}</div><span class="card-label">${label}</span>`;

    btn.addEventListener('click', () => handleCardClick(cardDef));
    sidebar.appendChild(btn);
  }

  // Divider before point rule section
  const divider = document.createElement('div');
  divider.className = 'sidebar-divider';
  sidebar.appendChild(divider);

  // Create Point Rule button
  const prBtn = document.createElement('button');
  prBtn.className = 'sidebar-card';
  prBtn.id = 'btn-create-point-rule';
  prBtn.disabled = !parentFunctionType;
  prBtn.title = 'Define how points transform: (x,y) \u2192 (x\u2032,y\u2032)';
  prBtn.innerHTML = `<div class="card-icon">${GuidedTopics.AffordanceCatalog['point-rule'].icon}</div><span class="card-label">Create point rule</span>`;
  prBtn.addEventListener('click', () => openPointRuleCreation());
  sidebar.appendChild(prBtn);

  // Graph Point Rule button
  const gptBtn = document.createElement('button');
  gptBtn.className = 'sidebar-card';
  gptBtn.id = 'btn-graph-point-transform';
  gptBtn.disabled = !parentFunctionType || !pointRuleExpression;
  gptBtn.title = 'Transform points on the parent curve using your point rule';
  gptBtn.innerHTML = `<div class="card-icon">${GuidedTopics.AffordanceCatalog['graph-point-transformation'].icon}</div><span class="card-label">Graph point rule</span>`;
  gptBtn.addEventListener('click', () => openGraphPointTransformation());
  sidebar.appendChild(gptBtn);

  // Transform in Graph button (draw target curve)
  const graphTransformBtn = document.createElement('button');
  graphTransformBtn.className =
    'sidebar-card' + (graphTransformState.active ? ' graph-transform-active' : '');
  graphTransformBtn.id = 'btn-graph-transform';
  graphTransformBtn.disabled =
    !parentFunctionType || PARENT_FUNCTIONS[parentFunctionType]?.isGeneric;
  graphTransformBtn.title =
    'Draw a target curve on the graph, then match it with transformations';
  const graphTransformIcon = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M3 18 Q8 6 12 12 Q16 18 21 8"/>
        <path d="M3 14 Q8 2 12 8 Q16 14 21 4" stroke-dasharray="2 2" opacity="0.6"/>
      </svg>`;
  const graphTransformLabel = graphTransformState.active
    ? 'Clear target curve'
    : 'Draw target curve';
  graphTransformBtn.innerHTML = `<div class="card-icon">${graphTransformIcon}</div><span class="card-label">${graphTransformLabel}</span>`;
  graphTransformBtn.addEventListener('click', () => toggleGraphTransformMode());
  sidebar.appendChild(graphTransformBtn);

  // Rewrite Formula button (rewrite/simplify)
  const tfBtn = document.createElement('button');
  tfBtn.className = 'sidebar-card';
  tfBtn.id = 'btn-rewrite-formula';
  tfBtn.disabled = !parentFunctionType;
  tfBtn.title = 'Simplify or rearrange any formula on the canvas';
  tfBtn.innerHTML = `<div class="card-icon">${GuidedTopics.AffordanceCatalog['rewrite-formula']?.icon || 'f\u2192f\u2032'}</div><span class="card-label">Rewrite formula</span>`;
  tfBtn.addEventListener('click', () => {
    if (transformFunctionFlow) transformFunctionFlow.open();
  });
  sidebar.appendChild(tfBtn);

  // Undo + Reset row (at the bottom after all cards)
  const actionRow = document.createElement('div');
  actionRow.className = 'sidebar-action-row';
  actionRow.innerHTML =
    `<button class="sidebar-action-btn" id="btn-undo" disabled>Undo</button>` +
    `<button class="sidebar-action-btn reset-btn" id="btn-reset" disabled>Reset All</button>`;
  sidebar.appendChild(actionRow);

  document.getElementById('btn-undo').addEventListener('click', undoLastTransformation);
  document.getElementById('btn-reset').addEventListener('click', resetAllTransformations);

  // Keyboard shortcut: Cmd+Z (Mac) or Ctrl+Z (Windows/Linux) for undo
  document.addEventListener('keydown', (e) => {
    // Check for Cmd+Z (Mac) or Ctrl+Z (Windows/Linux)
    if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
      // Don't trigger if user is typing in an input or if a modal is open
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (document.querySelector('.modal-canvas-overlay')) return;

      const undoBtn = document.getElementById('btn-undo');
      if (undoBtn && !undoBtn.disabled) {
        e.preventDefault();
        undoLastTransformation();
      }
    }
  });
}

function handleCardClick(cardDef) {
  if (cardDef.type === 'select-parent') {
    startChooser ? startChooser.show() : openParentFunctionOverlay();
    return;
  }
  openTransformModalCanvas(cardDef.type);
}

function updateSidebarCardStates() {
  const hasParent = !!parentFunctionType;
  TRANSFORM_CARDS.forEach((def) => {
    if (!def || !def.requiresParent) return;
    const el = document.getElementById(def.id);
    if (el) {
      el.disabled = !hasParent;
      if (!hasParent) el.title = 'Select a parent function first';
      else el.removeAttribute('title');
    }
  });
  // Point Rule button requires a parent; label changes when rule exists
  const prBtn = document.getElementById('btn-create-point-rule');
  if (prBtn) {
    prBtn.disabled = !hasParent;
    const labelSpan = prBtn.querySelector('.card-label');
    if (labelSpan) {
      labelSpan.textContent = pointRuleExpression
        ? 'Change point rule'
        : 'Create point rule';
    }
  }
  // GPT button requires a parent AND a point rule
  const gptBtn = document.getElementById('btn-graph-point-transform');
  if (gptBtn) gptBtn.disabled = !hasParent || !pointRuleExpression;
  // Rewrite Formula button requires a parent
  const tfBtn = document.getElementById('btn-rewrite-formula');
  if (tfBtn) tfBtn.disabled = !hasParent;
  // Transform in Graph button requires a parent and non-generic function
  const graphTransformBtn = document.getElementById('btn-graph-transform');
  if (graphTransformBtn) {
    graphTransformBtn.disabled =
      !hasParent || PARENT_FUNCTIONS[parentFunctionType]?.isGeneric;
    const labelSpan = graphTransformBtn.querySelector('.card-label');
    if (labelSpan) {
      labelSpan.textContent = graphTransformState.active
        ? 'Clear target curve'
        : 'Draw target curve';
    }
    graphTransformBtn.classList.toggle(
      'graph-transform-active',
      graphTransformState.active
    );
  }
}

function updateUndoResetButtons() {
  const hasHistory = chainState.history.length > 0;
  const undoBtn = document.getElementById('btn-undo');
  const resetBtn = document.getElementById('btn-reset');
  if (undoBtn) undoBtn.disabled = !hasHistory;
  if (resetBtn) resetBtn.disabled = !hasHistory;
}

// ========== TRANSFORMATION MODALS (ModalCanvas-based) ==========
const TRANSFORM_MODAL_CONFIGS = {
  'h-shift': {
    title: 'Horizontal Shift',
    ruleAscii: 'x\\to x-\\numberbox',
    hasPlaceholder: true,
    dragSource: 'x',
  },
  'v-shift': {
    title: 'Vertical Shift',
    ruleAscii: 'f[x]\\to f[x]+\\numberbox',
    hasPlaceholder: true,
    dragSource: 'f(x)',
  },
  'v-stretch': {
    title: 'Vertical Stretch',
    ruleAscii: 'f[x]\\to \\numberbox*f[x]',
    hasPlaceholder: true,
    dragSource: 'f(x)',
  },
  'h-stretch': {
    title: 'Horizontal Stretch',
    ruleAscii: 'x\\to \\numberbox*x',
    hasPlaceholder: true,
    dragSource: 'x',
  },
  'reflect-x': {
    title: 'Reflect across x-axis',
    ruleAscii: 'f[x]\\to -f[x]',
    hasPlaceholder: false,
    dragSource: 'f(x)',
  },
  'reflect-y': {
    title: 'Reflect across y-axis',
    ruleAscii: 'x\\to -x',
    hasPlaceholder: false,
    dragSource: 'x',
  },
};

function openTransformModalCanvas(type) {
  const cfg = TRANSFORM_MODAL_CONFIGS[type];
  if (!cfg) return;

  const currentFormula = getCurrentResultFormula();
  if (!currentFormula) {
    showInfoMessage('Select a parent function first.');
    return;
  }

  const isFirst = chainState.history.length === 0;
  const funcLabel = isFirst ? 'f' : getStepDisplayLabel(chainState.stepIndex - 1);
  const funcColor = isFirst
    ? { r: 108, g: 117, b: 125 }
    : GuidedTopics.DEFAULT_COLOR_PALETTE[
        (chainState.stepIndex - 1) % GuidedTopics.DEFAULT_COLOR_PALETTE.length
      ];

  let instructions;
  if (cfg.hasPlaceholder) {
    instructions =
      '<p><strong>Step 1:</strong> Click the box (\u2B1A) in the rule to enter a number.</p>' +
      '<p><strong>Step 2:</strong> Drag <em>' +
      cfg.dragSource +
      '</em> from the rule onto the function to apply.</p>';
  } else {
    instructions =
      '<p>Drag <em>' +
      cfg.dragSource +
      '</em> from the rule onto the function to apply the transformation.</p>';
  }

  // NumberBoxKeypad creates its own floating keypad DOM
  const customZones = [];

  const modal = new GuidedTopics.Workflows.ModalCanvas({
    title: getTransformLabel(type),
    expressions: [
      { ascii: cfg.ruleAscii, position: { x: 70, y: 50 }, rowAlignment: 'left' },
      {
        ascii: currentFormula,
        label: funcLabel,
        color: funcColor,
        position: { x: 70, y: 150 },
        rowAlignment: 'left',
      },
    ],
    customZones: customZones,
    instructions: instructions,
    detection: {
      check: createTransformDetector(),
      onDetected: (result, ctx) => {
        const nextLabel = getStepDisplayLabel(chainState.stepIndex);
        const doneBtn = ctx.modal._el?.querySelector('.btn-success');
        if (doneBtn) doneBtn.textContent = 'Save as ' + nextLabel;
      },
    },
    doneButton: {
      separateCancel: true,
      validationMessage: 'Finish making your new function first.',
    },
    // Disable auto-simplification in transform modals to avoid confusion during entry
    getDerivationSettings: () => ({
      auto_simplify_distributions: false,
      auto_simplify_multiplicative_identities: false,
      auto_simplify_additive_identities: false,
      auto_simplify_multiplicative_annihilators: false,
    }),
    applyEquationMode: () => applyEquationInteractionsMode(getCurrentEquationMode()),
    helpButton: {
      show: true,
      handler: (ctx) =>
        GuidedTopics.showHelpForModal(type, ctx.canvas, hintLookup, _helpOpts),
    },
    onCanvasReady: (modalCanvas, ctx) => {
      activeModalCanvas = modalCanvas;
      activeModalAffordance = type;

      // Auto-register for session recording if active
      if (GuidedTopics.isSessionActive?.()) {
        GuidedTopics.registerPanel(modalCanvas, type);
      }

      if (cfg.hasPlaceholder) {
        hookNumberBoxToKeypad(ctx, type);
      }
      const helpSetting = document.getElementById('setting-auto-help');
      if (helpSetting?.checked) {
        setTimeout(
          () => GuidedTopics.showHelpForModal(type, modalCanvas, hintLookup, _helpOpts),
          1500
        );
      }
      const timeline = modalCanvas.controller?.canvasTimeline?.();
      if (timeline) {
        timeline.on('newLocalTransaction', () => {
          const hs = document.getElementById('setting-auto-help');
          if (hs?.checked) {
            setTimeout(
              () =>
                GuidedTopics.showHelpForModal(type, modalCanvas, hintLookup, _helpOpts),
              800
            );
          }
        });
      }
    },
    onClose: () => {
      activeModalCanvas = null;
      activeModalAffordance = null;
    },
    onDone: (ctx) => {
      if (ctx.detectionResult) {
        handleTransformResult(type, ctx);
      }
    },
  });
  modal.open();
}

// Shared NumberBoxKeypad instance for transform modals
const numberBoxKeypad = new GuidedTopics.GuidedEntry.NumberBoxKeypad({
  keypadLayout: 'numeric-4col',
});

function hookNumberBoxToKeypad(ctx, transformType) {
  const derivations = ctx.derivations;
  if (!derivations || derivations.length === 0) return;

  // Find the rule derivation (the first one contains the transform rule)
  const ruleDl = derivations[0];

  // Determine validation rules based on transform type
  let validationRules;
  if (transformType === 'v-stretch' || transformType === 'h-stretch') {
    // Stretches must be positive (no zero, no negatives), allow decimals and fractions
    validationRules = [
      { type: 'positive', label: 'stretch factor', allowFraction: true },
    ];
  } else {
    // Shifts: allow any value (including negatives)
    // Use integer or decimal based on settings
    const integerOnly = document.getElementById('setting-integer-shifts')?.checked;
    validationRules = [
      {
        type: integerOnly ? 'integer' : 'decimal',
        label: 'shift value',
        allowFraction: !integerOnly,
      },
    ];
  }

  // Attach validation rules
  GuidedTopics.GuidedEntry.attachNumberBoxValidation(ruleDl, validationRules);

  // Wire NumberBox to use the keypad
  GuidedTopics.GuidedEntry.wireNumberBoxesToKeypad(ruleDl, numberBoxKeypad);
}

function createTransformDetector() {
  // Capture the actual to_ascii() of all DLs on first poll to avoid
  // string-format mismatches between manually constructed formulas and
  // the parser's serialization.
  let initialAsciiSet = null;

  return function (canvas, derivations) {
    if (!initialAsciiSet) {
      initialAsciiSet = new Set();
      for (const dl of derivations) {
        try {
          initialAsciiSet.add(dl.getLastRow().model.to_ascii());
        } catch (e) {
          /* skip */
        }
      }
      return null;
    }

    for (const dl of derivations) {
      try {
        const ascii = dl.getLastRow().model.to_ascii();
        if (initialAsciiSet.has(ascii)) continue;
        if (ascii.includes('\\to')) continue;
        if (ascii.includes('\\numberbox')) continue;
        if (ascii.includes('\u2B1A')) continue;
        const eqIdx = ascii.indexOf('=');
        if (eqIdx === -1) continue;
        const lhs = ascii.slice(0, eqIdx).trim();
        if (!lhs.includes('[')) continue;
        return { ascii };
      } catch (e) {
        continue;
      }
    }
    return null;
  };
}

function extractTransformValue(type, ruleAscii) {
  const ftStr = '\\to';
  const ftIdx = ruleAscii.indexOf(ftStr);
  if (ftIdx === -1) return null;
  const rhs = ruleAscii.slice(ftIdx + ftStr.length).trim();

  // Helper: strip one layer of outer parens
  const stripParens = (s) => {
    s = s.trim();
    if (s.startsWith('(') && s.endsWith(')')) s = s.slice(1, -1).trim();
    return s;
  };

  // Helper: parse a number that might be a fraction like "1/2" or "-3/4"
  const parseNumeric = (s) => {
    s = stripParens(s.trim());
    // Check for fraction: numerator/denominator
    const fracMatch = s.match(/^(-?\d+(?:\.\d+)?)\s*\/\s*(-?\d+(?:\.\d+)?)$/);
    if (fracMatch) {
      const num = parseFloat(fracMatch[1]);
      const den = parseFloat(fracMatch[2]);
      if (isNaN(num) || isNaN(den) || den === 0) return NaN;
      return num / den;
    }
    return parseFloat(s);
  };

  switch (type) {
    case 'h-shift': {
      // RHS: (x-3) or (x+3) or x-3
      const inner = stripParens(rhs);
      const m = inner.match(/^x\s*([+-])\s*(.+)$/);
      if (!m) return null;
      const val = parseNumeric(m[2]);
      if (isNaN(val)) return null;
      return m[1] === '-' ? val : -val;
    }
    case 'v-shift': {
      // RHS: f[x]+3 or f[x]-3
      const bracketEnd = rhs.indexOf(']');
      if (bracketEnd === -1) return null;
      const after = rhs.slice(bracketEnd + 1).trim();
      const m = after.match(/^([+-])\s*(.+)$/);
      if (!m) return null;
      const val = parseNumeric(m[2]);
      if (isNaN(val)) return null;
      return m[1] === '+' ? val : -val;
    }
    case 'v-stretch': {
      // RHS: 2*f[x] or (1/2)*f[x] or -f[x]
      const fIdx = rhs.indexOf('f[');
      if (fIdx === -1) return null;
      let before = rhs.slice(0, fIdx).trim();
      if (before.endsWith('*')) before = before.slice(0, -1).trim();
      if (before === '-') return -1;
      if (before === '' || before === '+') return 1;
      const val = parseNumeric(before);
      return isNaN(val) ? null : val;
    }
    case 'h-stretch': {
      // RHS: 2*x or (1/2)*x or -x
      const inner = stripParens(rhs);
      const xIdx = inner.lastIndexOf('x');
      if (xIdx === -1) return null;
      let before = inner.slice(0, xIdx).trim();
      if (before.endsWith('*')) before = before.slice(0, -1).trim();
      if (before === '-') return -1;
      if (before === '' || before === '+') return 1;
      const val = parseNumeric(before);
      return isNaN(val) ? null : val;
    }
    default:
      return null;
  }
}

function handleTransformResult(type, ctx) {
  const cfg = TRANSFORM_MODAL_CONFIGS[type];

  if (cfg.hasPlaceholder) {
    // Extract the parameter value from the rule DL
    let value = null;
    for (const dl of ctx.derivations) {
      try {
        const ascii = dl.getLastRow().model.to_ascii();
        if (ascii.includes('\\to')) {
          value = extractTransformValue(type, ascii);
          break;
        }
      } catch (e) {
        /* skip */
      }
    }

    if (value !== null) {
      applyTransformation(type, value);
    } else {
      console.warn('[' + type + '] Could not extract value from rule; using 0');
      applyTransformation(type, 0);
    }
  } else {
    // Reflections: no parameter value
    applyTransformation(type, null);
  }

  // Get the CURRENT state of the result derivation (respects user simplifications)
  // Filter: result DL has '=' but not '\to' (that's the rule DL)
  const resultAscii = ctx.getCurrentResultAscii(
    (ascii) => ascii.includes('=') && !ascii.includes('\\to')
  );

  // Extract the body and construct the final saved form.
  const eqIdx = resultAscii.indexOf('=');
  const body = eqIdx !== -1 ? resultAscii.slice(eqIdx + 1).trim() : resultAscii;
  const nextGMLabel = getStepGMLabel(chainState.stepIndex - 1);
  const finalAscii = nextGMLabel + '[x] = ' + body;

  addChainStepDL(finalAscii);
  onChainChanged();
}

function getCurrentResultFormula() {
  if (!parentFunctionType) return '';

  // Query the canvas model directly to get the CURRENT state of the last step.
  // IMPORTANT: Use getLastRow().model.to_ascii(), NOT dl.root.to_ascii()
  // - dl.root = ORIGINAL tree when DL was created
  // - dl.getLastRow().model = CURRENT tree after all user manipulations
  // Also: "Rewrite formula" creates a NEW DL, so when multiple DLs match
  // the target label, take the LAST one (most recently added).
  if (chainState.history.length > 0 && canvas?.model) {
    const targetLabel = getStepGMLabel(chainState.stepIndex - 1); // e.g., "f_1"
    const allDLs = canvas.model.getDerivations?.() || [];

    const matchingDLs = [];
    for (const dl of allDLs) {
      try {
        const lastRow = dl.getLastRow?.();
        const ascii = lastRow?.model?.to_ascii?.();
        if (ascii && ascii.startsWith(targetLabel + '[')) {
          matchingDLs.push({ dl, ascii });
        }
      } catch (e) {
        /* skip */
      }
    }

    // If multiple DLs match, take the LAST one (most recently added)
    if (matchingDLs.length > 0) {
      return matchingDLs[matchingDLs.length - 1].ascii;
    }
  }

  // Fallback to chainDLs if canvas query didn't work
  if (chainDLs.length > 0) {
    const lastEntry = chainDLs[chainDLs.length - 1];
    // Use getLastRow().model for current state, not root
    if (lastEntry.derivation?.getLastRow) {
      try {
        const ascii = lastEntry.derivation.getLastRow().model.to_ascii();
        if (ascii) return ascii;
      } catch (e) {
        /* skip */
      }
    }
    if (lastEntry.formula) {
      return lastEntry.formula;
    }
  }

  // Final fallback: compute from params
  const parentFormula = PARENT_FUNCTIONS[parentFunctionType].formula;
  if (chainState.history.length === 0) {
    return 'f[x] = ' + parentFormula;
  }
  const result = computeTransformedFormula(parentFormula, chainState.params);
  const label = getStepGMLabel(chainState.stepIndex - 1);
  return label + '[x] = ' + result;
}

// ========== CHAIN HISTORY ON GM CANVAS ==========
/**
 * @param {string} [resultAsciiOverride] - If provided, use this ASCII (from the user's
 *   actual work in a ModalCanvas) instead of pre-computing via computeTransformedFormula.
 */
function addChainStepDL(resultAsciiOverride) {
  if (!parentFunctionType || !canvas?.model) return;

  const p = { ...chainState.params }; // snapshot — chainState.params is mutated in place
  const stepIdx = chainState.history.length - 1;
  const gmLabel = getStepGMLabel(stepIdx);
  const displayLabel = getStepDisplayLabel(stepIdx);

  let displayFormula, graphFormula, resultFormula;
  if (resultAsciiOverride) {
    // Use the actual result from the user's work in the modal
    displayFormula = resultAsciiOverride;
    const eqIdx = resultAsciiOverride.indexOf('=');
    resultFormula =
      eqIdx !== -1 ? resultAsciiOverride.slice(eqIdx + 1).trim() : resultAsciiOverride;
    graphFormula = 'y = ' + resultFormula;
  } else {
    // Fallback: compute from params (legacy path for non-modal transforms)
    const parentFormula = PARENT_FUNCTIONS[parentFunctionType].formula;
    resultFormula = computeTransformedFormula(parentFormula, p);
    displayFormula = gmLabel + '[x] = ' + resultFormula;
    graphFormula = 'y = ' + resultFormula;
  }

  const lastStep = chainState.history[chainState.history.length - 1];

  const yPos = CHAIN_START_Y + stepIdx * CHAIN_STEP_SPACING;
  const color =
    GuidedTopics.DEFAULT_COLOR_PALETTE[
      stepIdx % GuidedTopics.DEFAULT_COLOR_PALETTE.length
    ];

  // Push the entry to chainDLs synchronously so morph play buttons appear
  // immediately, even before the async DL creation callback fires.
  const entry = {
    label: displayLabel,
    derivation: null, // filled in by createDL callback
    row: null,
    formula: displayFormula,
    expression: displayFormula,
    graphFormula: graphFormula,
    type: 'transformed-function',
    color,
    params: { ...p },
    labelButton: null, // filled in by createDL callback
  };
  chainDLs.push(entry);
  if (typeof updateMorphPlayButtons === 'function') updateMorphPlayButtons();

  canvas.model.createDL(
    {
      eq: displayFormula,
      pos: { x: GuidedTopics.EXPRESSION_START_X, y: yPos },
    },
    (createdDL) => {
      GuidedTopics.animateExpressionEntrance?.(createdDL);
      setTimeout(() => {
        fitExpressionToWidth(createdDL, GuidedTopics.EXPRESSION_START_X, yPos); // Scale down if too wide
        const labelX = createdDL.pos.x - 35;
        const labelY = createdDL.pos.y + (createdDL.size?.height || 40) / 2 - 17.5;
        const labelBtn = exprSystem.createLabelButton(
          displayLabel,
          labelX,
          labelY,
          color
        );
        // Add subscript class for multi-char labels (not needed in letter mode)
        if (!isLetterNamingMode()) labelBtn.classList.add('subscript-label');

        // Create reason overlay next to the equation (two-column proof style)
        const reasonText = lastStep
          ? getStepDescription(lastStep.type, lastStep.value)
          : '';
        const reasonEl = reasonText ? createReasonOverlay(reasonText, labelY) : null;

        // Update the entry with DL-dependent fields
        entry.derivation = createdDL;
        entry.row = createdDL.getLastRow();
        entry.labelButton = labelBtn;
        entry.reasonEl = reasonEl;

        expressions.push(entry);
        if (exprSystem) exprSystem.registerExpression(entry);
        exprSystem.syncEntities();
        if (typeof updateMorphPlayButtons === 'function') updateMorphPlayButtons();

        // Add observation
        if (lastStep) {
          const stepDesc =
            lastStep.type.replace('-', ' ') +
            (lastStep.value != null ? ': ' + lastStep.value : '');
          observationStore.addObservation({
            type: 'transformation',
            displayText: `Applied ${stepDesc} \u2192 ${displayLabel}[x] = ${resultFormula}`,
            displayParts: [
              { text: 'Applied ' + stepDesc + ': ' },
              { entity: displayLabel },
              { text: '[x] = ' + resultFormula },
            ],
            data: {
              transformType: lastStep.type,
              value: lastStep.value,
              resultFormula: displayFormula,
              params: { ...p },
            },
          });
        }
      }, 200);
    }
  );
}

function removeLastChainDL() {
  if (chainDLs.length === 0) return;
  const last = chainDLs.pop();

  if (last.labelButton) {
    const container = last.labelButton.parentElement;
    if (container) container.remove();
  }
  if (last.reasonEl) last.reasonEl.remove();
  try {
    canvas.model.removeElement(last.derivation);
  } catch (e) {}

  const idx = expressions.indexOf(last);
  if (idx !== -1) expressions.splice(idx, 1);
  exprSystem.syncEntities();
}

function removeAllChainDLs() {
  while (chainDLs.length > 0) {
    removeLastChainDL();
  }
}

// ========== MODAL UTILITIES (shared) ==========
const { createModalCanvas, addModalLabel } = GuidedTopics.Workflows;

// ========== MORPH INTERPOLATION ANIMATION ==========
// Play buttons on the result graph panel let the user animate a smooth
// morph from one chain step's curve to the next.

const morphState = {
  active: false,
  animFrameId: null,
  tempCurveName: null,
  _onDone: null,
};

// Timing constants (ms) — adjusted for clearer visualization
const MORPH_HOLD_FROM = 400; // Time to hold on "from" state before morphing
const MORPH_DURATION = 1800; // Duration of the morph animation
const MORPH_HOLD_TO = 1200; // Time to hold on "to" state after morphing

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function cancelMorphAnimation() {
  if (morphState.animFrameId) {
    cancelAnimationFrame(morphState.animFrameId);
    morphState.animFrameId = null;
  }
  if (morphState.tempCurveName && jsxManagerResult) {
    try {
      jsxManagerResult.removeElement(morphState.tempCurveName);
    } catch (e) {}
    morphState.tempCurveName = null;
  }
  if (morphState._onDone) {
    morphState._onDone();
    morphState._onDone = null;
  }
  morphState.active = false;
}

function finalizeMorphAnimation(curveName) {
  morphState.active = false;
  morphState.animFrameId = null;

  if (curveName && jsxManagerResult) {
    try {
      jsxManagerResult.removeElement(curveName);
    } catch (e) {}
  }
  morphState.tempCurveName = null;

  // Ensure the result graph shows the target (updateResultGraph handles this)
  if (morphState._onDone) {
    morphState._onDone();
    morphState._onDone = null;
  }
}

/**
 * Evaluate a transformed curve at x given transformation params and the
 * current parent function.
 *
 * @param {{a,b,h,k}} params
 * @param {number} x
 * @returns {number}
 */
function evalCurve(params, x) {
  const parentEval = PARENT_FUNCTIONS[parentFunctionType]?.eval;
  if (!parentEval) return NaN;
  const inner = params.b * (x - params.h);
  return params.a * parentEval(inner) + params.k;
}

/**
 * Return a function that maps a point (x, y) on the "from" curve to its
 * destination on the "to" curve, based on the transformation type.
 *
 * Each transformation has a clean geometric point mapping:
 *   h-shift(v)   → (x+v, y)      horizontal slide
 *   v-shift(v)   → (x, y+v)      vertical slide
 *   h-stretch(f) → (x/f, y)      horizontal scale toward x=0
 *   v-stretch(f) → (x, f·y)      vertical scale toward y=0
 *   reflect-x    → (x, -y)       vertical flip
 *   reflect-y    → (-x, y)       horizontal flip
 *
 * @param {string} type   transformation type from chainState.history
 * @param {number} value  transformation parameter (ignored for reflections)
 * @returns {(x: number, y: number) => [number, number]}
 */
function getPointMapping(type, value) {
  switch (type) {
    case 'h-shift':
      return (x, y) => [x + value, y];
    case 'v-shift':
      return (x, y) => [x, y + value];
    case 'h-stretch':
      return (x, y) => [x / value, y];
    case 'v-stretch':
      return (x, y) => [x, value * y];
    case 'reflect-x':
      return (x, y) => [x, -y];
    case 'reflect-y':
      return (x, y) => [-x, y];
    default:
      return null;
  }
}

/**
 * Animate a morph on the result graph.
 *
 * When stepType/stepValue are provided, uses point-tracking mode: sample
 * points on the "from" curve, compute destinations via the point mapping,
 * and interpolate each point along its individual path.  This produces
 * geometrically meaningful intermediate frames (e.g. horizontal flip reads
 * as a fold across the y-axis, not a collapse through a=0).
 *
 * Falls back to parameter-interpolation mode when stepType is not provided.
 *
 * @param {{a,b,h,k}} fromParams
 * @param {{a,b,h,k}} toParams
 * @param {{r,g,b}} fromColor
 * @param {{r,g,b}} toColor
 * @param {string} [stepType]   transformation type (e.g. 'reflect-y')
 * @param {number} [stepValue]  transformation value (e.g. 2 for stretch)
 */
function playMorphAnimation(
  fromParams,
  toParams,
  fromColor,
  toColor,
  stepType,
  stepValue
) {
  if (morphState.active) cancelMorphAnimation();

  if (!jsxManagerResult?.isReady) return;
  const board = jsxManagerResult.board;
  if (!board) return;
  if (!parentFunctionType) return;

  const fc = fromColor || { r: 108, g: 117, b: 125 };
  const tc = toColor || { r: 66, g: 133, b: 244 };

  function lerpColor(t) {
    const r = Math.round(fc.r + t * (tc.r - fc.r));
    const g = Math.round(fc.g + t * (tc.g - fc.g));
    const b = Math.round(fc.b + t * (tc.b - fc.b));
    return `rgb(${r}, ${g}, ${b})`;
  }

  // Decide mode: point-tracking when we know the step type, else param-lerp
  const pointMap = stepType ? getPointMapping(stepType, stepValue) : null;

  if (pointMap) {
    _playPointTrackingMorph(board, fromParams, lerpColor, pointMap);
  } else {
    _playParamLerpMorph(board, fromParams, toParams, lerpColor);
  }
}

/**
 * Point-tracking morph: sample points on the "from" curve, compute
 * destinations via the geometric point mapping, and interpolate each
 * point along its straight-line path.
 */
function _playPointTrackingMorph(board, fromParams, lerpColor, pointMap) {
  const bb = board.getBoundingBox(); // [xMin, yMax, xMax, yMin]
  const margin = (bb[2] - bb[0]) * 0.1;
  const xMin = bb[0] - margin;
  const xMax = bb[2] + margin;
  const N = 300;
  const dx = (xMax - xMin) / N;

  // Sample points and compute destinations
  const samples = [];
  for (let i = 0; i <= N; i++) {
    const x = xMin + i * dx;
    const y = evalCurve(fromParams, x);
    if (!isFinite(y)) {
      samples.push(null); // preserve index for gap handling
      continue;
    }
    const [toX, toY] = pointMap(x, y);
    samples.push({ fromX: x, fromY: y, toX, toY });
  }

  // Build initial data arrays
  const dataX = new Array(samples.length);
  const dataY = new Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    if (samples[i]) {
      dataX[i] = samples[i].fromX;
      dataY[i] = samples[i].fromY;
    } else {
      dataX[i] = NaN;
      dataY[i] = NaN;
    }
  }

  const curveName = '__morph_anim__';
  morphState.tempCurveName = curveName;

  let curve;
  try {
    curve = board.create('curve', [dataX, dataY], {
      strokeColor: lerpColor(0),
      strokeWidth: 3,
      withLabel: false,
      highlight: false,
      fixed: true,
    });
  } catch (e) {
    console.warn('[Morph] Could not create point-tracking curve:', e);
    morphState.tempCurveName = null;
    return;
  }

  jsxManagerResult._elements.set(curveName, {
    element: curve,
    type: 'function',
    formula: '__morph_temp__',
  });

  morphState.active = true;
  const startTime = performance.now();

  function tick(now) {
    if (!morphState.active) return;
    const elapsed = now - startTime;

    let tParam;
    if (elapsed < MORPH_HOLD_FROM) {
      tParam = 0;
    } else if (elapsed < MORPH_HOLD_FROM + MORPH_DURATION) {
      const raw = (elapsed - MORPH_HOLD_FROM) / MORPH_DURATION;
      tParam = easeInOutCubic(Math.min(raw, 1));
    } else if (elapsed < MORPH_HOLD_FROM + MORPH_DURATION + MORPH_HOLD_TO) {
      tParam = 1;
    } else {
      finalizeMorphAnimation(curveName);
      return;
    }

    // Interpolate each sampled point along its straight-line path
    for (let i = 0; i < samples.length; i++) {
      const s = samples[i];
      if (!s) continue;
      dataX[i] = s.fromX + tParam * (s.toX - s.fromX);
      dataY[i] = s.fromY + tParam * (s.toY - s.fromY);
    }

    try {
      curve.dataX = dataX;
      curve.dataY = dataY;
      curve.setAttribute({ strokeColor: lerpColor(tParam) });
      curve.needsUpdate = true;
      curve.updateCurve();
      board.update();
    } catch (e) {
      // Swallow JSXGraph rendering errors for intermediate frames
    }
    morphState.animFrameId = requestAnimationFrame(tick);
  }

  morphState.animFrameId = requestAnimationFrame(tick);
}

/**
 * Parameter-interpolation morph (original approach): linearly interpolate
 * {a, b, h, k} and recompile the formula each frame.  Used as fallback
 * when the transformation type is not known.
 */
function _playParamLerpMorph(board, fromParams, toParams, lerpColor) {
  const pFormula = PARENT_FUNCTIONS[parentFunctionType].formula;
  const fromP = fromParams;
  const toP = toParams;

  // Compile the initial "from" function
  const fromFormulaStr = computeTransformedFormula(pFormula, fromP);
  const fromRhs = jsxManagerResult._convertNotation(fromFormulaStr);
  let currentFn;
  try {
    currentFn = board.jc.snippet(fromRhs, true, 'x', true);
  } catch (e) {
    console.warn('[Morph] Could not compile from formula:', e);
    return;
  }

  const graphFn = (x) => {
    try {
      return currentFn(x);
    } catch (e) {
      return NaN;
    }
  };

  const curveName = '__morph_anim__';
  morphState.tempCurveName = curveName;

  let curve;
  try {
    curve = board.create('functiongraph', [graphFn], {
      strokeColor: lerpColor(0),
      strokeWidth: 3,
      withLabel: false,
      highlight: false,
      fixed: true,
    });
  } catch (e) {
    console.warn('[Morph] Could not create functiongraph:', e);
    morphState.tempCurveName = null;
    return;
  }

  jsxManagerResult._elements.set(curveName, {
    element: curve,
    type: 'function',
    formula: '__morph_temp__',
  });

  morphState.active = true;
  const startTime = performance.now();

  function tick(now) {
    if (!morphState.active) return;
    const elapsed = now - startTime;

    let tParam;
    if (elapsed < MORPH_HOLD_FROM) {
      tParam = 0;
    } else if (elapsed < MORPH_HOLD_FROM + MORPH_DURATION) {
      const raw = (elapsed - MORPH_HOLD_FROM) / MORPH_DURATION;
      tParam = easeInOutCubic(Math.min(raw, 1));
    } else if (elapsed < MORPH_HOLD_FROM + MORPH_DURATION + MORPH_HOLD_TO) {
      tParam = 1;
    } else {
      finalizeMorphAnimation(curveName);
      return;
    }

    const lerpedParams = {
      a: fromP.a + tParam * (toP.a - fromP.a),
      b: fromP.b + tParam * (toP.b - fromP.b),
      h: fromP.h + tParam * (toP.h - fromP.h),
      k: fromP.k + tParam * (toP.k - fromP.k),
    };

    try {
      const formula = computeTransformedFormula(pFormula, lerpedParams);
      const converted = jsxManagerResult._convertNotation(formula);
      currentFn = board.jc.snippet(converted, true, 'x', true);
    } catch (e) {
      // If compilation fails for an intermediate frame, skip update
    }

    try {
      curve.setAttribute({ strokeColor: lerpColor(tParam) });
      curve.needsUpdate = true;
      curve.updateCurve();
      board.update();
    } catch (e) {
      // Swallow JSXGraph rendering errors for intermediate frames
    }
    morphState.animFrameId = requestAnimationFrame(tick);
  }

  morphState.animFrameId = requestAnimationFrame(tick);
}

// ---- Play-button controls on the result graph panel ----

function updateMorphPlayButtons() {
  const container = document.getElementById('morph-play-controls');
  if (!container) return;
  container.innerHTML = '';

  if (chainDLs.length === 0) return;

  chainDLs.forEach((entry, idx) => {
    // "from" is the parent (identity) for the first step, otherwise the previous chain step
    let fromLabel, fromParams, fromColor;
    if (idx === 0) {
      fromLabel = 'f';
      fromParams = { a: 1, b: 1, h: 0, k: 0 };
      fromColor = { r: 108, g: 117, b: 125 };
    } else {
      const prev = chainDLs[idx - 1];
      fromLabel = prev.label;
      fromParams = prev.params;
      fromColor = prev.color;
    }

    if (!fromParams || !entry.params) return;

    // Look up the transformation step so we can use point-tracking morph
    const historyStep = chainState.history[idx];
    const stepType = historyStep?.type;
    const stepValue = historyStep?.value;

    const btn = document.createElement('button');
    btn.className = 'morph-play-btn';
    btn.title = `Animate ${fromLabel} \u2192 ${entry.label}`;
    if (entry.color) {
      btn.style.background = `rgb(${entry.color.r}, ${entry.color.g}, ${entry.color.b})`;
    }
    btn.innerHTML = `<span class="play-icon">&#9654;</span>${fromLabel} \u2192 ${entry.label}`;

    btn.addEventListener('click', () => {
      container
        .querySelectorAll('.morph-play-btn')
        .forEach((b) => b.classList.remove('playing'));
      btn.classList.add('playing');

      if (morphState.active) cancelMorphAnimation();
      morphState._onDone = () => {
        btn.classList.remove('playing');
      };

      playMorphAnimation(
        fromParams,
        entry.params,
        fromColor,
        entry.color,
        stepType,
        stepValue
      );
    });

    container.appendChild(btn);
  });
}

// ========== CREATE POINT RULE ==========
function initPointRuleFlow() {
  pointRuleFlow = new GuidedTopics.PointRuleFlow({
    container: document.body,
    prefix: 'rst-prf',
    onSubmit: (expression, ruleType, parsedValues) => {
      pointRuleExpression = expression;

      // Build evaluators from the expression
      const eqs = GuidedTopics.extractEquationsFromRule(expression);
      if (eqs) {
        pointRuleEvaluators = {
          evaluateXRule: GuidedTopics.makeRuleEvaluator(eqs.xEq, 'x'),
          evaluateYRule: GuidedTopics.makeRuleEvaluator(eqs.yEq, 'y'),
        };
      }

      // Show point rule in result box and enable GPT button
      updateResultFunctionBox();
      updateSidebarCardStates();

      // Clear existing arrows since the rule changed
      clearGptArrows();
    },
  });
}

function openPointRuleCreation() {
  if (!parentFunctionType) {
    showInfoMessage('Select a parent function first.');
    return;
  }
  if (!pointRuleFlow) initPointRuleFlow();
  pointRuleFlow.open();
}

// ========== GRAPH POINT TRANSFORMATION ==========
const gptState = {
  active: false,
  arrows: [], // { parentPtName, resultPtName, srcEchoName, arrowName }
  counter: 0,
  abortController: null,
  _banner: null,
};

function openGraphPointTransformation() {
  if (!parentFunctionType) {
    showInfoMessage('Select a parent function first.');
    return;
  }
  if (!pointRuleExpression || !pointRuleEvaluators) {
    showInfoMessage('Create a point rule first.');
    return;
  }
  if (!jsxManagerParent?.isReady || !jsxManagerResult?.isReady) {
    showInfoMessage('Graph is still loading. Please wait.');
    return;
  }

  gptState.active = true;
  gptState.abortController = new AbortController();
  const signal = gptState.abortController.signal;

  // Disable pan on parent graph for click-to-place
  jsxManagerParent.setPanEnabled(false);

  // Show instruction banner on the parent graph
  const container = document.getElementById('jsxgraph-container-parent');
  const banner = document.createElement('div');
  banner.className = 'tracking-banner';
  banner.innerHTML =
    '<span class="tracking-banner-text">Click on the parent graph to transform points</span>' +
    '<button class="tracking-banner-btn tracking-banner-btn--done">Done</button>';
  container.appendChild(banner);
  gptState._banner = banner;

  banner
    .querySelector('.tracking-banner-btn--done')
    .addEventListener('click', closeGraphPointTransformation, { signal });

  // Register click handler on parent graph
  container.addEventListener('pointerdown', _handleGptClick, { signal });
  container.style.cursor = 'crosshair';

  _updateClearArrowsBtn();
}

function _handleGptClick(e) {
  if (!gptState.active) return;
  // Ignore clicks on the banner itself
  if (e.target.closest('.tracking-banner')) return;

  const container = document.getElementById('jsxgraph-container-parent');
  const rect = container.getBoundingClientRect();
  const bb = jsxManagerParent.board.getBoundingBox(); // [xMin, yMax, xMax, yMin]

  const localX = e.clientX - rect.left;
  const mathX = bb[0] + (localX * (bb[2] - bb[0])) / container.offsetWidth;

  // Snap to nearest 0.5 for clean points
  const snapX = Math.round(mathX * 2) / 2;

  // Compute parent y = f(snapX)
  const parentEval = PARENT_FUNCTIONS[parentFunctionType]?.eval;
  if (!parentEval) return;
  const parentY = parentEval(snapX);
  if (isNaN(parentY) || !isFinite(parentY)) return;

  // Compute transformed point using point rule evaluators
  if (!pointRuleEvaluators) return;
  const transformedX = pointRuleEvaluators.evaluateXRule(snapX);
  const transformedY = pointRuleEvaluators.evaluateYRule(parentY);
  if (
    isNaN(transformedX) ||
    !isFinite(transformedX) ||
    isNaN(transformedY) ||
    !isFinite(transformedY)
  )
    return;

  gptState.counter++;
  const idx = gptState.counter;

  // Source point on parent graph
  const parentPtName = 'GptSrc' + idx;
  jsxManagerParent.createPoint(snapX, parentY, {
    name: parentPtName,
    color: '#6b7280',
    pointSize: 4,
    fixed: true,
    withLabel: false,
  });

  // Echo of source point on result graph (dimmed)
  const srcEchoName = 'GptSrcEcho' + idx;
  jsxManagerResult.createPoint(snapX, parentY, {
    name: srcEchoName,
    color: '#9ca3af',
    pointSize: 3,
    fixed: true,
    withLabel: false,
  });

  // Target point on result graph
  const resultPtName = 'GptDst' + idx;
  jsxManagerResult.createPoint(transformedX, transformedY, {
    name: resultPtName,
    color: '#7c3aed',
    pointSize: 5,
    fixed: true,
    withLabel: false,
  });

  // Straight arrow on result graph from source to target
  const arrowName = 'GptArrow' + idx;
  jsxManagerResult.createCurvedArrow(snapX, parentY, transformedX, transformedY, {
    name: arrowName,
    color: '#9333ea',
    strokeWidth: 2,
    dash: 2,
    curvature: 0,
  });

  gptState.arrows.push({ parentPtName, resultPtName, srcEchoName, arrowName });
  _updateClearArrowsBtn();
}

function closeGraphPointTransformation() {
  if (!gptState.active) return;
  gptState.active = false;

  if (gptState.abortController) {
    gptState.abortController.abort();
    gptState.abortController = null;
  }
  if (gptState._banner) {
    gptState._banner.remove();
    gptState._banner = null;
  }

  jsxManagerParent?.setPanEnabled(true);
  const container = document.getElementById('jsxgraph-container-parent');
  if (container) container.style.cursor = '';
}

function clearGptArrows() {
  gptState.arrows.forEach((a) => {
    try {
      jsxManagerParent?.removeElement(a.parentPtName);
    } catch (e) {}
    try {
      jsxManagerResult?.removeElement(a.resultPtName);
    } catch (e) {}
    try {
      jsxManagerResult?.removeElement(a.srcEchoName);
    } catch (e) {}
    try {
      jsxManagerResult?.removeElement(a.arrowName);
    } catch (e) {}
  });
  gptState.arrows = [];
  _updateClearArrowsBtn();
}

function _updateClearArrowsBtn() {
  const btn = document.getElementById('btn-clear-gpt-arrows');
  if (btn) btn.style.display = gptState.arrows.length > 0 ? '' : 'none';
}

// Wire up the permanent "Clear arrows" button (once DOM is ready)
document.addEventListener('DOMContentLoaded', () => {
  const clearBtn = document.getElementById('btn-clear-gpt-arrows');
  if (clearBtn) clearBtn.addEventListener('click', clearGptArrows);
});

// ========== GRAPH TRANSFORM MODE ==========
const graphTransformState = {
  active: false,
  params: { a: 1, b: 1, h: 0, k: 0 },
  reflectX: false,
  reflectY: false,
  targetCurveName: null,
  translationHandle: null,
  stretchHandle: null, // single handle controlling both a and b
  refLineV: null,
  refLineH: null,
  labelA: null,
  labelB: null,
  abortController: null,
  _banner: null,
};

function enterGraphTransformMode() {
  if (graphTransformState.active) return;
  if (!parentFunctionType) {
    showInfoMessage('Select a parent function first.');
    return;
  }
  if (!jsxManagerResult?.isReady) {
    showInfoMessage('Graph is still loading. Please wait.');
    return;
  }

  graphTransformState.active = true;
  graphTransformState.abortController = new AbortController();

  // Initialize target with a slight offset so it's visible
  graphTransformState.params = { a: 1, b: 0.5, h: 3, k: 2 };
  graphTransformState.reflectX = false;
  graphTransformState.reflectY = false;

  // Disable pan on result graph while dragging control points
  jsxManagerResult.setPanEnabled(false);

  // Create control points and target curve
  createGraphTransformControlPoints();
  updateTargetCurve();

  // Show banner
  showGraphTransformBanner();

  // Setup keyboard shortcuts
  setupGraphTransformKeyboardHandler();

  // Update sidebar button state
  updateGraphTransformSidebarButton();
}

function exitGraphTransformMode({ removeCurve = false } = {}) {
  if (!graphTransformState.active) return;
  graphTransformState.active = false;

  // Abort keyboard listener
  if (graphTransformState.abortController) {
    graphTransformState.abortController.abort();
    graphTransformState.abortController = null;
  }

  // Remove control points
  removeGraphTransformControlPoints();

  // Remove target curve (orange graph) only if explicitly requested
  if (removeCurve && graphTransformState.targetCurveName) {
    try {
      jsxManagerResult.removeElement(graphTransformState.targetCurveName);
    } catch (e) {}
    graphTransformState.targetCurveName = null;
  }

  // Remove banner
  if (graphTransformState._banner) {
    graphTransformState._banner.remove();
    graphTransformState._banner = null;
  }

  // Re-enable pan
  jsxManagerResult?.setPanEnabled(true);

  // Update sidebar button state
  updateGraphTransformSidebarButton();
}

function toggleGraphTransformMode() {
  if (graphTransformState.active) {
    // Currently editing → exit but keep curve visible
    exitGraphTransformMode({ removeCurve: false });
  } else if (graphTransformState.targetCurveName) {
    // Not editing but curve is visible → remove it
    if (graphTransformState.targetCurveName) {
      try {
        jsxManagerResult.removeElement(graphTransformState.targetCurveName);
      } catch (e) {}
      graphTransformState.targetCurveName = null;
    }
    updateGraphTransformSidebarButton();
  } else {
    // No curve → enter edit mode
    enterGraphTransformMode();
  }
}

/** Adaptive snapping: coarser steps for large values, finer for small.
 *  |val| >= 1  → snap to 0.5
 *  |val| >= 0.1 → snap to 0.1
 *  |val| < 0.1 → snap to 0.01 */
function adaptiveSnap(val) {
  const abs = Math.abs(val);
  if (abs >= 1) return Math.round(val * 2) / 2; // 0.5 increments
  if (abs >= 0.1) return Math.round(val * 10) / 10; // 0.1 increments
  return Math.round(val * 100) / 100; // 0.01 increments
}

/** Get the reference point (refX, refY) for the current parent function.
 *  refX is the canonical input, refY = f(refX). Falls back to (1, f(1)). */
function getGraphTransformRef() {
  const info = PARENT_FUNCTIONS[parentFunctionType];
  if (!info) return { refX: 1, refY: 1 };
  const refX = info.refX ?? 1;
  const refY = info.refY ?? (info.eval ? info.eval(refX) : 1);
  return { refX, refY };
}

function createGraphTransformControlPoints() {
  const board = jsxManagerResult?.board;
  if (!board) return;

  const { a, b, h, k } = graphTransformState.params;
  const { refX, refY } = getGraphTransformRef();

  // Stretch handle position: ON the curve at the transformed reference point
  const stretchX = refX / b + h;
  const stretchY = a * refY + k;

  // --- Translation handle (orange) — drag to move h and k ---
  const translationPt = board.create('point', [h, k], {
    name: '',
    size: 6,
    strokeColor: '#f59e0b',
    fillColor: '#fef3c7',
    fixed: false,
    snapToGrid: true,
    snapSizeX: 0.5,
    snapSizeY: 0.5,
    showInfobox: false,
  });

  translationPt.on('drag', () => {
    const coords = translationPt.coords.usrCoords;
    graphTransformState.params.h = Math.round(coords[1] * 2) / 2;
    graphTransformState.params.k = Math.round(coords[2] * 2) / 2;
    updateTargetCurve();
    updateStretchHandlePosition();
  });

  graphTransformState.translationHandle = translationPt;

  // --- Stretch handle (red) — ON the curve, controls both a and b ---
  const stretchPt = board.create('point', [stretchX, stretchY], {
    name: '',
    size: 5,
    strokeColor: '#dc2626',
    fillColor: '#fecaca',
    fixed: false,
    snapToGrid: false,
    showInfobox: false,
  });

  stretchPt.on('drag', () => {
    const coords = stretchPt.coords.usrCoords;
    const { refX: rX, refY: rY } = getGraphTransformRef();
    const { h: pH, k: pK } = graphTransformState.params;

    // b = refX / (x - h) — adaptive snap: 0.5 when |b|>=1, 0.1 when |b|>=0.1, 0.01 below
    const dx = coords[1] - pH;
    if (Math.abs(dx) >= 0.05) {
      const rawB = rX / dx;
      const snappedB = adaptiveSnap(rawB);
      if (Math.abs(snappedB) >= 0.01 && Math.abs(snappedB) <= 10) {
        graphTransformState.params.b = snappedB;
      }
    }

    // a = (y - k) / refY — adaptive snap: 0.5 when |a|>=1, 0.1 when |a|>=0.1, 0.01 below
    const rawA = (coords[2] - pK) / (rY || 1);
    const snappedA = adaptiveSnap(rawA);
    if (Math.abs(snappedA) >= 0.01) {
      graphTransformState.params.a = snappedA;
    }

    updateTargetCurve();
    updateStretchHandlePosition();
  });

  graphTransformState.stretchHandle = stretchPt;

  // --- Corner point (invisible, for reference line routing) ---
  // Sits at (stretchX, k) — the "elbow" between horizontal and vertical ref lines
  const cornerPt = board.create(
    'point',
    [
      () => {
        const { refX: rX } = getGraphTransformRef();
        return rX / graphTransformState.params.b + graphTransformState.params.h;
      },
      () => graphTransformState.params.k,
    ],
    {
      name: '',
      size: 0,
      visible: false,
      fixed: true,
      showInfobox: false,
    }
  );

  // --- Reference lines ---
  // Horizontal: from translation (h,k) to corner (stretchX, k)
  graphTransformState.refLineH = board.create('segment', [translationPt, cornerPt], {
    strokeColor: '#2563eb',
    strokeWidth: 1.5,
    dash: 2,
    strokeOpacity: 0.4,
    fixed: true,
    highlight: false,
  });

  // Vertical: from corner (stretchX, k) to stretch handle (stretchX, a*refY+k)
  graphTransformState.refLineV = board.create('segment', [cornerPt, stretchPt], {
    strokeColor: '#dc2626',
    strokeWidth: 1.5,
    dash: 2,
    strokeOpacity: 0.4,
    fixed: true,
    highlight: false,
  });

  // --- Parameter labels ---
  graphTransformState.labelA = board.create(
    'text',
    [
      () => {
        const { refX: rX } = getGraphTransformRef();
        return rX / graphTransformState.params.b + graphTransformState.params.h + 0.3;
      },
      () => {
        const { refY: rY } = getGraphTransformRef();
        return graphTransformState.params.k + (graphTransformState.params.a * rY) / 2;
      },
      () => `a=${graphTransformState.params.a}`,
    ],
    {
      fontSize: 12,
      fontFamily: "'SF Mono', 'Fira Code', monospace",
      anchorX: 'left',
      anchorY: 'middle',
      color: '#dc2626',
      strokeColor: 'none',
      highlight: false,
      fixed: true,
      cssStyle: 'font-weight: 600; opacity: 0.75; pointer-events: none;',
    }
  );

  graphTransformState.labelB = board.create(
    'text',
    [
      () => {
        const { refX: rX } = getGraphTransformRef();
        return graphTransformState.params.h + rX / graphTransformState.params.b / 2;
      },
      () => graphTransformState.params.k - 0.4,
      () => `b=${graphTransformState.params.b}`,
    ],
    {
      fontSize: 12,
      fontFamily: "'SF Mono', 'Fira Code', monospace",
      anchorX: 'middle',
      anchorY: 'top',
      color: '#2563eb',
      strokeColor: 'none',
      highlight: false,
      fixed: true,
      cssStyle: 'font-weight: 600; opacity: 0.75; pointer-events: none;',
    }
  );
}

function removeGraphTransformControlPoints() {
  const board = jsxManagerResult?.board;
  if (!board) return;

  const keys = [
    'translationHandle',
    'stretchHandle',
    'refLineV',
    'refLineH',
    'labelA',
    'labelB',
  ];
  for (const key of keys) {
    if (graphTransformState[key]) {
      try {
        board.removeObject(graphTransformState[key]);
      } catch (e) {}
      graphTransformState[key] = null;
    }
  }
}

function updateStretchHandlePosition() {
  const { a, b, h, k } = graphTransformState.params;
  const { refX, refY } = getGraphTransformRef();
  const ptX = refX / b + h;

  if (graphTransformState.stretchHandle) {
    graphTransformState.stretchHandle.setPosition(JXG.COORDS_BY_USER, [
      ptX,
      a * refY + k,
    ]);
  }

  // Force board update so segments, corner point, and labels follow
  jsxManagerResult?.board?.update();
}

function updateTargetCurve() {
  if (!jsxManagerResult?.isReady || !parentFunctionType) return;

  // Remove existing target curve
  if (graphTransformState.targetCurveName) {
    try {
      jsxManagerResult.removeElement(graphTransformState.targetCurveName);
    } catch (e) {}
    graphTransformState.targetCurveName = null;
  }

  // Get parent function info
  const parentInfo = PARENT_FUNCTIONS[parentFunctionType];
  if (!parentInfo || parentInfo.isGeneric) return; // Can't graph generic f(x)

  // Compute effective params with reflections
  const effectiveA = graphTransformState.reflectX
    ? -graphTransformState.params.a
    : graphTransformState.params.a;
  const effectiveB = graphTransformState.reflectY
    ? -graphTransformState.params.b
    : graphTransformState.params.b;
  const { h, k } = graphTransformState.params;

  let result;

  if (parentInfo.isDiscreteData && parentInfo.points) {
    // Data points: transform each point and graph piecewise curve
    const transformedPoints = parentInfo.points.map((p) => ({
      x: p.x / effectiveB + h,
      y: effectiveA * p.y + k,
    }));
    result = jsxManagerResult.graphPiecewiseCurve(transformedPoints, {
      name: 'graph_transform_target',
      color: '#f59e0b',
      strokeWidth: 3,
      dash: 3,
      showPoints: true,
      pointSize: 4,
    });
  } else {
    // Standard formula: build and graph
    const targetFormula = buildTransformedFormulaFromParams(
      parentInfo.formula,
      effectiveA,
      effectiveB,
      h,
      k
    );
    result = jsxManagerResult.graphFormula('y=' + targetFormula, {
      name: 'graph_transform_target',
      color: '#f59e0b',
      strokeWidth: 3,
      dash: 3,
      withLabel: false,
    });
  }

  if (result) {
    graphTransformState.targetCurveName = result.name;
  }
}

function buildTransformedFormulaFromParams(parentFormula, a, b, h, k) {
  // Build: a * f(b*(x-h)) + k
  // This is a simplified version - we'll construct the string directly
  let inner = parentFormula; // e.g., 'x^2', '|x|', 'sqrt[x]'

  // Apply horizontal transformations: replace x with b*(x-h)
  let xExpr = 'x';
  if (h !== 0) {
    xExpr = h > 0 ? `(x-${h})` : `(x+${Math.abs(h)})`;
  }
  if (b !== 1) {
    xExpr = b === -1 ? `(-${xExpr.replace(/^\(|\)$/g, '')})` : `(${b}*${xExpr})`;
  }

  // Replace x in the formula with the transformed x
  let transformed = inner.replace(/x/g, xExpr);

  // Apply vertical transformations
  if (a !== 1) {
    if (a === -1) {
      transformed = `-(${transformed})`;
    } else {
      transformed = `${a}*(${transformed})`;
    }
  }

  if (k !== 0) {
    transformed = k > 0 ? `(${transformed})+${k}` : `(${transformed})${k}`;
  }

  return transformed;
}

function showGraphTransformBanner() {
  const container = document.getElementById('jsxgraph-container-result');
  const banner = document.createElement('div');
  banner.className = 'graph-transform-banner';

  banner.innerHTML = `
        <span class="graph-transform-banner-text">Drag the target curve</span>
        <span class="graph-transform-reflections">
          <button class="graph-transform-reflect-btn" id="btn-graph-transform-reflect-x" title="Reflect across x-axis (R)">↕</button>
          <button class="graph-transform-reflect-btn" id="btn-graph-transform-reflect-y" title="Reflect across y-axis (F)">↔</button>
        </span>
        <button class="graph-transform-banner-btn graph-transform-btn-reset">Reset</button>
        <button class="graph-transform-banner-btn graph-transform-btn-done">Done</button>
      `;
  container.appendChild(banner);
  graphTransformState._banner = banner;

  const signal = graphTransformState.abortController.signal;

  banner
    .querySelector('.graph-transform-btn-done')
    .addEventListener('click', exitGraphTransformMode, { signal });
  banner
    .querySelector('.graph-transform-btn-reset')
    .addEventListener('click', resetGraphTransformTarget, { signal });

  banner.querySelector('#btn-graph-transform-reflect-x').addEventListener(
    'click',
    () => {
      graphTransformState.reflectX = !graphTransformState.reflectX;
      updateTargetCurve();
      updateGraphTransformReflectButtons();
    },
    { signal }
  );

  banner.querySelector('#btn-graph-transform-reflect-y').addEventListener(
    'click',
    () => {
      graphTransformState.reflectY = !graphTransformState.reflectY;
      updateTargetCurve();
      updateGraphTransformReflectButtons();
    },
    { signal }
  );
}

function updateGraphTransformReflectButtons() {
  const btnX = document.getElementById('btn-graph-transform-reflect-x');
  const btnY = document.getElementById('btn-graph-transform-reflect-y');
  if (btnX) btnX.classList.toggle('active', graphTransformState.reflectX);
  if (btnY) btnY.classList.toggle('active', graphTransformState.reflectY);
}

function resetGraphTransformTarget() {
  graphTransformState.params = { a: 1, b: 0.5, h: 3, k: 2 };
  graphTransformState.reflectX = false;
  graphTransformState.reflectY = false;

  // Update control point positions
  if (graphTransformState.translationHandle) {
    graphTransformState.translationHandle.setPosition(JXG.COORDS_BY_USER, [
      graphTransformState.params.h,
      graphTransformState.params.k,
    ]);
  }
  updateStretchHandlePosition();
  updateTargetCurve();
  updateGraphTransformReflectButtons();
}

function setupGraphTransformKeyboardHandler() {
  const signal = graphTransformState.abortController.signal;

  document.addEventListener(
    'keydown',
    (e) => {
      if (!graphTransformState.active) return;
      // Don't trigger if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      const key = e.key.toLowerCase();
      if (key === 'r') {
        graphTransformState.reflectX = !graphTransformState.reflectX;
        updateTargetCurve();
        updateGraphTransformReflectButtons();
      } else if (key === 'f') {
        graphTransformState.reflectY = !graphTransformState.reflectY;
        updateTargetCurve();
        updateGraphTransformReflectButtons();
      }
    },
    { signal }
  );
}

function updateGraphTransformSidebarButton() {
  const btn = document.getElementById('btn-graph-transform');
  if (btn) {
    // Button shows as active if curve is visible (whether or not in edit mode)
    const hasCurve = !!graphTransformState.targetCurveName;
    btn.classList.toggle(
      'graph-transform-active',
      graphTransformState.active || hasCurve
    );

    // Update label based on state
    const labelSpan = btn.querySelector('.card-label');
    if (labelSpan) {
      labelSpan.textContent = hasCurve ? 'Clear target curve' : 'Draw target curve';
    }
  }
}

// ========== INIT ==========
initCanvas();
